/**
 * AnimationEngine Component
 * Handles player animations using Framer Motion
 * Requirements: 5.2, 6.3, 7.4
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Position, RouteType, RBRouteType, CoverageType, FormationType, Waypoint, CoverageReaction, DBLBReaction, FSReaction, CB2Reaction, DBLB2Reaction, SSReaction, LBReaction, PositionDescription, PlayerPositionId, CustomDefenseAssignments } from '../types';
import { 
  getRouteWaypoints, 
  getRouteDuration, 
  getRouteDefinition,
  getAvailableRoutes,
  isValidRoute,
  getRBRouteWaypoints,
  getRBRouteDuration,
  getRBRouteDefinition,
  getAvailableRBRoutes,
  isValidRBRoute
} from '../rules/routeRules';
import { 
  isValidCoverage,
  getCoverageDefinition,
  getCB1StartPosition as getCB1StartPositionFromRules,
  getDBLB1StartPosition as getDBLB1StartPositionFromRules,
  getFSStartPosition as getFSStartPositionFromRules,
  getCB2StartPosition as getCB2StartPositionFromRules,
  getDBLB2StartPosition as getDBLB2StartPositionFromRules,
  getSSStartPosition as getSSStartPositionFromRules,
  getLBStartPosition as getLBStartPositionFromRules,
  getCoverageReaction,
  getDBLB1Reaction,
  getFSReaction,
  getCB2Reaction,
  getDBLB2Reaction,
  getSSReaction,
  getLBReaction,
  buildObservationFeed,
  resolveManMatchDetailed,
  resolveUnderneathMatchDetailed,
  resolveHookReadDetailed,
  resolveApexMatchDetailed,
  resolveDeepZone,
  resolveManTrailDetailed,
  resolvePalmsCornerDetailed,
  MIN_SIM_HORIZON_MS,
  DEFAULT_MAN_MATCH_PARAMS_RIGHT,
  DEFAULT_MAN_MATCH_PARAMS_FS,
  DEFAULT_MAN_MATCH_PARAMS_SS,
  DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1,
  DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2,
  DEFAULT_HOOK_READ_PARAMS_LB,
  DEFAULT_MAN_MATCH_PARAMS_CB1_COVER3,
  DEFAULT_MAN_MATCH_PARAMS_CB2_COVER3,
  DEFAULT_APEX_MATCH_PARAMS_DBLB1,
  DEFAULT_APEX_MATCH_PARAMS_SS,
  DEFAULT_HOOK_READ_PARAMS_LB_COVER3,
  DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER3,
  DEFAULT_DEEP_ZONE_PARAMS_FS,
  DEFAULT_MAN_TRAIL_PARAMS,
  DEFAULT_APEX_MATCH_PARAMS_DBLB1_TRIPS,
  DEFAULT_APEX_MATCH_PARAMS_LB_TRIPS,
  DEFAULT_HOOK_READ_PARAMS_DBLB2_TRIPS,
  DEFAULT_HOOK_READ_PARAMS_SS_TRIPS,
  DEFAULT_DEEP_ZONE_PARAMS_SS_COVER6,
  DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER6,
  DEFAULT_PALMS_CORNER_PARAMS_CB1,
  DEFAULT_PALMS_CORNER_PARAMS_CB2,
  DEFAULT_DEEP_ZONE_PARAMS_SS_PALMS,
  DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1_PALMS,
  DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2_PALMS,
  resolvePalmsSafetyDetailed,
  DEFAULT_PALMS_SAFETY_PARAMS_FS,
  DEFAULT_PALMS_SAFETY_PARAMS_SS,
  resolvePositionMovement,
  PositionMovementResolution
} from '../rules';
import { getPositionDescription } from '../utils/positionDescriptions';
import type { DefensePositionId } from '../types';
import type { ManMatchResult, ReadState, ReadTraceEntry } from '../rules';
import './AnimationEngine.css';

/**
 * Check if a route is an in-breaking crossing route (slant or drag).
 * Used by coverage logic to determine matching behavior.
 */
function isInBreakingRoute(route: string): boolean {
  return route === 'slant' || route === 'drag';
}

/**
 * The skill players whose route can be changed from the field.
 *
 * Only these five carry a route picker; every other glyph on the field is either a defender or a
 * non-routing offensive player (C, QB).
 */
type RoutePickerId = 'wr1' | 'wr2' | 'wr3' | 'wr4' | 'rb';

interface AnimationEngineProps {
  isPlaying: boolean;
  selectedFormation?: FormationType;
  selectedRoute: RouteType;
  selectedRoute2: RouteType;
  selectedRoute3: RouteType;
  selectedRoute4: RouteType;
  selectedRBRoute: RBRouteType;
  selectedCoverage: CoverageType;
  /**
   * When present, drives all 7 defensive positions via `resolvePositionMovement` instead of
   * the legacy whole-team `selectedCoverage` branching. When absent (every built-in coverage
   * today), behavior is 100% unchanged. Always resolved against `selectedFormation`, never a
   * defense's saved formation (Req 5.1, 5.2).
   */
  customDefenseAssignments?: CustomDefenseAssignments;
  /**
   * Development-only diagnostic hook. Called once per play with each position's resolved read,
   * for the dev overlay. Only passed when the debug flag is on, so it costs nothing otherwise.
   */
  onReadTrace?: (reads: Partial<Record<DefensePositionId, { finalState: ReadState; trace: ReadTraceEntry[] }>>) => void;
  playbackSpeed?: number;
  onAnimationComplete: () => void;
  onWRPositionUpdate?: (position: Position) => void;
  onWR2PositionUpdate?: (position: Position) => void;
  onWR3PositionUpdate?: (position: Position) => void;
  onWR4PositionUpdate?: (position: Position) => void;
  onRBPositionUpdate?: (position: Position) => void;
  onCB1PositionUpdate?: (position: Position) => void;
  onCB2PositionUpdate?: (position: Position) => void;
  onDBLB1PositionUpdate?: (position: Position) => void;
  onDBLB2PositionUpdate?: (position: Position) => void;
  onFSPositionUpdate?: (position: Position) => void;
  onSSPositionUpdate?: (position: Position) => void;
  onLBPositionUpdate?: (position: Position) => void;
  onWR1RouteChange?: (route: RouteType) => void;
  onWR2RouteChange?: (route: RouteType) => void;
  onWR3RouteChange?: (route: RouteType) => void;
  onWR4RouteChange?: (route: RouteType) => void;
  onRBRouteChange?: (route: RBRouteType) => void;
}

/**
 * AnimationEngine component
 * Orchestrates WR route execution and CB reaction animations
 * Integrates route rules (Req 6.3) and coverage rules (Req 7.4)
 */
const AnimationEngine: React.FC<AnimationEngineProps> = ({
  isPlaying,
  selectedFormation = 'doubles',
  selectedRoute,
  selectedRoute2,
  selectedRoute3,
  selectedRoute4,
  selectedRBRoute,
  selectedCoverage,
  customDefenseAssignments,
  onReadTrace,
  playbackSpeed = 1,
  onAnimationComplete,
  onWRPositionUpdate,
  onWR2PositionUpdate,
  onWR3PositionUpdate,
  onWR4PositionUpdate,
  onRBPositionUpdate,
  onCB1PositionUpdate,
  onCB2PositionUpdate,
  onDBLB1PositionUpdate,
  onDBLB2PositionUpdate,
  onFSPositionUpdate,
  onSSPositionUpdate,
  onLBPositionUpdate,
  onWR1RouteChange,
  onWR2RouteChange,
  onWR3RouteChange,
  onWR4RouteChange,
  onRBRouteChange,
}) => {
  const wrControls = useAnimation();
  const wr2Controls = useAnimation();
  const wr3Controls = useAnimation();
  const wr4Controls = useAnimation();
  const rbControls = useAnimation();
  const cb1Controls = useAnimation();
  const cb2Controls = useAnimation();
  const dblb1Controls = useAnimation();
  const dblb2Controls = useAnimation();
  const fsControls = useAnimation();
  const ssControls = useAnimation();
  const lbControls = useAnimation();
  const animationRef = useRef<boolean>(false);

  /**
   * Validate and load route data from route rules system
   * Requirements: 6.3 - Reference route rules when executing WR movements
   */
  const loadRouteData = useCallback((route: RouteType): { waypoints: Waypoint[]; duration: number } | null => {
    // Validate route exists in the rules system
    if (!isValidRoute(route)) {
      console.error(`Invalid route type: ${route}`);
      return null;
    }

    const routeDefinition = getRouteDefinition(route);
    if (!routeDefinition || !routeDefinition.waypoints || routeDefinition.waypoints.length === 0) {
      console.error(`Route definition not found or invalid for: ${route}`);
      return null;
    }

    return {
      waypoints: getRouteWaypoints(route),
      duration: getRouteDuration(route),
    };
  }, []);

  /**
   * Validate and load RB route data from route rules system
   */
  const loadRBRouteData = useCallback((route: RBRouteType): { waypoints: Waypoint[]; duration: number } | null => {
    // Validate route exists in the rules system
    if (!isValidRBRoute(route)) {
      console.error(`Invalid RB route type: ${route}`);
      return null;
    }

    const waypoints = getRBRouteWaypoints(route);
    if (!waypoints || waypoints.length === 0) {
      console.error(`RB route waypoints not found for: ${route}`);
      return null;
    }

    return {
      waypoints,
      duration: getRBRouteDuration(route),
    };
  }, []);

  /**
   * Validate and load coverage data from coverage rules system
   * Requirements: 7.4 - Reference coverage rules when executing CB movements
   */
  const loadCoverageData = useCallback((coverage: CoverageType, route: RouteType): {
    startPosition: Position;
    reaction: CoverageReaction | undefined;
  } | null => {
    // Validate coverage exists in the rules system
    if (!isValidCoverage(coverage, selectedFormation)) {
      console.error(`Invalid coverage type: ${coverage}`);
      return null;
    }

    const coverageDefinition = getCoverageDefinition(coverage, selectedFormation);
    if (!coverageDefinition) {
      console.error(`Coverage definition not found for: ${coverage}`);
      return null;
    }

    // Use centralized coverage rules functions for CB1 behavior
    const startPosition = getCB1StartPositionFromRules(coverage, selectedFormation);
    const reaction = getCoverageReaction(coverage, route, selectedFormation);

    return { startPosition, reaction };
  }, [selectedFormation]);

  /**
   * Get CB1 starting position based on coverage type
   * Uses centralized coverage rules system for position data
   */
  const getCB1StartPosition = useCallback((): Position => {
    return getCB1StartPositionFromRules(selectedCoverage, selectedFormation);
  }, [selectedCoverage, selectedFormation]);

  /**
   * Get DB/LB1 starting position based on coverage type
   */
  const getDBLB1StartPosition = useCallback((): Position => {
    return getDBLB1StartPositionFromRules(selectedCoverage, selectedFormation);
  }, [selectedCoverage, selectedFormation]);

  /**
   * Get FS starting position based on coverage type
   */
  const getFSStartPosition = useCallback((): Position => {
    return getFSStartPositionFromRules(selectedCoverage, selectedFormation);
  }, [selectedCoverage, selectedFormation]);

  /**
   * Get CB2 starting position based on coverage type
   */
  const getCB2StartPosition = useCallback((): Position => {
    return getCB2StartPositionFromRules(selectedCoverage, selectedFormation);
  }, [selectedCoverage, selectedFormation]);

  /**
   * Get DB/LB2 starting position based on coverage type
   */
  const getDBLB2StartPosition = useCallback((): Position => {
    return getDBLB2StartPositionFromRules(selectedCoverage, selectedFormation);
  }, [selectedCoverage, selectedFormation]);

  /**
   * Get SS starting position based on coverage type
   */
  const getSSStartPosition = useCallback((): Position => {
    return getSSStartPositionFromRules(selectedCoverage, selectedFormation);
  }, [selectedCoverage, selectedFormation]);

  /**
   * Get LB starting position based on coverage type
   */
  const getLBStartPosition = useCallback((): Position => {
    return getLBStartPositionFromRules(selectedCoverage, selectedFormation);
  }, [selectedCoverage, selectedFormation]);

  /**
   * Convert waypoints to Framer Motion animation sequence
   */
  const createWRAnimation = useCallback((waypoints: Waypoint[], controls: ReturnType<typeof useAnimation>, onPositionUpdate?: (position: Position) => void, speed: number = 1): Promise<void> => {
    return new Promise(async (resolve) => {
      for (let i = 1; i < waypoints.length; i++) {
        const waypoint = waypoints[i];
        const prevWaypoint = waypoints[i - 1];
        const duration = (waypoint.timing - prevWaypoint.timing) / 1000 / speed;

        await controls.start({
          left: `${waypoint.position.x}%`,
          top: `${waypoint.position.y}%`,
          transition: {
            duration,
            ease: 'linear',
          },
        });

        // Update position callback
        if (onPositionUpdate) {
          onPositionUpdate(waypoint.position);
        }
      }
      resolve();
    });
  }, []);

  /**
   * Create CB1 reaction animation based on coverage rules
   */
  const createCB1Animation = useCallback(
    async (reaction: CoverageReaction, routeDuration: number, startPosition: Position, speed: number = 1): Promise<void> => {
      // Include start position as first point for smooth animation
      const allPositions = [startPosition, ...reaction.cbResponse];
      
      if (allPositions.length < 2) return;

      // Calculate total animation time (adjusted for playback speed)
      const totalDuration = routeDuration / 1000 / speed;

      // Calculate distances between each waypoint
      const distances: number[] = [];
      for (let i = 1; i < allPositions.length; i++) {
        const dx = allPositions[i].x - allPositions[i - 1].x;
        const dy = allPositions[i].y - allPositions[i - 1].y;
        distances.push(Math.sqrt(dx * dx + dy * dy));
      }
      const totalDistance = distances.reduce((sum, d) => sum + d, 0);

      // Calculate times based on distance (proportional timing)
      const times: number[] = [0];
      let cumulativeDistance = 0;
      for (const distance of distances) {
        cumulativeDistance += distance;
        times.push(cumulativeDistance / totalDistance);
      }

      // Animate through all positions smoothly using keyframes
      const keyframes = {
        left: allPositions.map(p => `${p.x}%`),
        top: allPositions.map(p => `${p.y}%`),
      };

      await cb1Controls.start({
        left: keyframes.left,
        top: keyframes.top,
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });

      // Update position callback with final position
      if (onCB1PositionUpdate && reaction.cbResponse.length > 0) {
        onCB1PositionUpdate(reaction.cbResponse[reaction.cbResponse.length - 1]);
      }
    },
    [cb1Controls, onCB1PositionUpdate]
  );

  /**
   * Animate CB1 through a resolved, perception-based trajectory.
   *
   * Unlike createCB1Animation (which uses distance-proportional timing and thus assumes
   * constant speed), this uses UNIFORM timing across the samples. The trajectory comes
   * from a fixed-timestep simulation, so speed is already encoded in the spacing between
   * samples — uniform playback preserves pauses, late reads, and acceleration faithfully.
   */
  const createCB1TrajectoryAnimation = useCallback(
    async (positions: Position[], horizonMs: number, speed: number = 1): Promise<void> => {
      if (positions.length < 2) return;

      const totalDuration = horizonMs / 1000 / speed;
      const n = positions.length;
      const times = positions.map((_, i) => i / (n - 1));

      await cb1Controls.start({
        left: positions.map((p) => `${p.x}%`),
        top: positions.map((p) => `${p.y}%`),
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });

      if (onCB1PositionUpdate) {
        onCB1PositionUpdate(positions[n - 1]);
      }
    },
    [cb1Controls, onCB1PositionUpdate]
  );

  /**
   * Create DB/LB1 reaction animation based on coverage rules
   */
  const createDBLB1Animation = useCallback(
    async (reaction: DBLBReaction, routeDuration: number, startPosition: Position, speed: number = 1): Promise<void> => {
      // Include start position as first point for smooth animation
      const allPositions = [startPosition, ...reaction.response];
      
      if (allPositions.length < 2) return;

      // Calculate total animation time (adjusted for playback speed)
      const totalDuration = routeDuration / 1000 / speed;

      // Calculate distances between each waypoint
      const distances: number[] = [];
      for (let i = 1; i < allPositions.length; i++) {
        const dx = allPositions[i].x - allPositions[i - 1].x;
        const dy = allPositions[i].y - allPositions[i - 1].y;
        distances.push(Math.sqrt(dx * dx + dy * dy));
      }
      const totalDistance = distances.reduce((sum, d) => sum + d, 0);

      // Calculate times based on distance (proportional timing)
      const times: number[] = [0];
      let cumulativeDistance = 0;
      for (const distance of distances) {
        cumulativeDistance += distance;
        times.push(cumulativeDistance / totalDistance);
      }

      // Animate through all positions smoothly using keyframes
      const keyframes = {
        left: allPositions.map(p => `${p.x}%`),
        top: allPositions.map(p => `${p.y}%`),
      };

      await dblb1Controls.start({
        left: keyframes.left,
        top: keyframes.top,
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });

      // Update position callback with final position
      if (onDBLB1PositionUpdate && reaction.response.length > 0) {
        onDBLB1PositionUpdate(reaction.response[reaction.response.length - 1]);
      }
    },
    [dblb1Controls, onDBLB1PositionUpdate]
  );

  /**
   * Animate DB/LB1 through a resolved, perception-based trajectory (uniform timing).
   * Same model as createCB1TrajectoryAnimation; DB/LB1 keys WR2 and runs the underneath
   * "Wall-Read on #2" read in Cover 4 Match.
   */
  const createDBLB1TrajectoryAnimation = useCallback(
    async (positions: Position[], horizonMs: number, speed: number = 1): Promise<void> => {
      if (positions.length < 2) return;
      const totalDuration = horizonMs / 1000 / speed;
      const n = positions.length;
      const times = positions.map((_, i) => i / (n - 1));
      await dblb1Controls.start({
        left: positions.map((p) => `${p.x}%`),
        top: positions.map((p) => `${p.y}%`),
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });
      if (onDBLB1PositionUpdate) {
        onDBLB1PositionUpdate(positions[n - 1]);
      }
    },
    [dblb1Controls, onDBLB1PositionUpdate]
  );

  /**
   * Create FS reaction animation based on coverage rules
   */
  const createFSAnimation = useCallback(
    async (reaction: FSReaction, routeDuration: number, startPosition: Position, speed: number = 1): Promise<void> => {
      // Include start position as first point for smooth animation
      const allPositions = [startPosition, ...reaction.response];
      
      if (allPositions.length < 2) return;

      // Calculate total animation time (adjusted for playback speed)
      const totalDuration = routeDuration / 1000 / speed;

      // Calculate distances between each waypoint
      const distances: number[] = [];
      for (let i = 1; i < allPositions.length; i++) {
        const dx = allPositions[i].x - allPositions[i - 1].x;
        const dy = allPositions[i].y - allPositions[i - 1].y;
        distances.push(Math.sqrt(dx * dx + dy * dy));
      }
      const totalDistance = distances.reduce((sum, d) => sum + d, 0);

      // Calculate times based on distance (proportional timing)
      const times: number[] = [0];
      let cumulativeDistance = 0;
      for (const distance of distances) {
        cumulativeDistance += distance;
        times.push(cumulativeDistance / totalDistance);
      }

      // Animate through all positions smoothly using keyframes
      const keyframes = {
        left: allPositions.map(p => `${p.x}%`),
        top: allPositions.map(p => `${p.y}%`),
      };

      await fsControls.start({
        left: keyframes.left,
        top: keyframes.top,
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });

      // Update position callback with final position
      if (onFSPositionUpdate && reaction.response.length > 0) {
        onFSPositionUpdate(reaction.response[reaction.response.length - 1]);
      }
    },
    [fsControls, onFSPositionUpdate]
  );

  /**
   * Create CB2 reaction animation based on coverage rules
   */
  const createCB2Animation = useCallback(
    async (reaction: CB2Reaction, routeDuration: number, startPosition: Position, speed: number = 1): Promise<void> => {
      // Include start position as first point for smooth animation
      const allPositions = [startPosition, ...reaction.cbResponse];
      
      if (allPositions.length < 2) return;

      // Calculate total animation time (adjusted for playback speed)
      const totalDuration = routeDuration / 1000 / speed;

      // Calculate distances between each waypoint
      const distances: number[] = [];
      for (let i = 1; i < allPositions.length; i++) {
        const dx = allPositions[i].x - allPositions[i - 1].x;
        const dy = allPositions[i].y - allPositions[i - 1].y;
        distances.push(Math.sqrt(dx * dx + dy * dy));
      }
      const totalDistance = distances.reduce((sum, d) => sum + d, 0);

      // Calculate times based on distance (proportional timing)
      const times: number[] = [0];
      let cumulativeDistance = 0;
      for (const distance of distances) {
        cumulativeDistance += distance;
        times.push(cumulativeDistance / totalDistance);
      }

      // Animate through all positions smoothly using keyframes
      const keyframes = {
        left: allPositions.map(p => `${p.x}%`),
        top: allPositions.map(p => `${p.y}%`),
      };

      await cb2Controls.start({
        left: keyframes.left,
        top: keyframes.top,
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });

      // Update position callback with final position
      if (onCB2PositionUpdate && reaction.cbResponse.length > 0) {
        onCB2PositionUpdate(reaction.cbResponse[reaction.cbResponse.length - 1]);
      }
    },
    [cb2Controls, onCB2PositionUpdate]
  );

  /**
   * Animate CB2 through a resolved, perception-based trajectory (uniform timing).
   * Right-side mirror of createCB1TrajectoryAnimation — see that function for why
   * uniform (not distance-proportional) timing is used.
   */
  const createCB2TrajectoryAnimation = useCallback(
    async (positions: Position[], horizonMs: number, speed: number = 1): Promise<void> => {
      if (positions.length < 2) return;

      const totalDuration = horizonMs / 1000 / speed;
      const n = positions.length;
      const times = positions.map((_, i) => i / (n - 1));

      await cb2Controls.start({
        left: positions.map((p) => `${p.x}%`),
        top: positions.map((p) => `${p.y}%`),
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });

      if (onCB2PositionUpdate) {
        onCB2PositionUpdate(positions[n - 1]);
      }
    },
    [cb2Controls, onCB2PositionUpdate]
  );

  /**
   * Animate FS through a resolved, perception-based trajectory (uniform timing).
   * Same model as createCB1TrajectoryAnimation; FS keys WR2.
   */
  const createFSTrajectoryAnimation = useCallback(
    async (positions: Position[], horizonMs: number, speed: number = 1): Promise<void> => {
      if (positions.length < 2) return;
      const totalDuration = horizonMs / 1000 / speed;
      const n = positions.length;
      const times = positions.map((_, i) => i / (n - 1));
      await fsControls.start({
        left: positions.map((p) => `${p.x}%`),
        top: positions.map((p) => `${p.y}%`),
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });
      if (onFSPositionUpdate) {
        onFSPositionUpdate(positions[n - 1]);
      }
    },
    [fsControls, onFSPositionUpdate]
  );

  /**
   * Animate SS through a resolved, perception-based trajectory (uniform timing).
   * Same model as createCB1TrajectoryAnimation; SS keys WR3.
   */
  const createSSTrajectoryAnimation = useCallback(
    async (positions: Position[], horizonMs: number, speed: number = 1): Promise<void> => {
      if (positions.length < 2) return;
      const totalDuration = horizonMs / 1000 / speed;
      const n = positions.length;
      const times = positions.map((_, i) => i / (n - 1));
      await ssControls.start({
        left: positions.map((p) => `${p.x}%`),
        top: positions.map((p) => `${p.y}%`),
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });
      if (onSSPositionUpdate) {
        onSSPositionUpdate(positions[n - 1]);
      }
    },
    [ssControls, onSSPositionUpdate]
  );

  /**
   * Create DB/LB2 reaction animation based on coverage rules
   */
  const createDBLB2Animation = useCallback(
    async (reaction: DBLB2Reaction, routeDuration: number, startPosition: Position, speed: number = 1): Promise<void> => {
      // Include start position as first point for smooth animation
      const allPositions = [startPosition, ...reaction.response];
      
      if (allPositions.length < 2) return;

      // Calculate total animation time (adjusted for playback speed)
      const totalDuration = routeDuration / 1000 / speed;

      // Calculate distances between each waypoint
      const distances: number[] = [];
      for (let i = 1; i < allPositions.length; i++) {
        const dx = allPositions[i].x - allPositions[i - 1].x;
        const dy = allPositions[i].y - allPositions[i - 1].y;
        distances.push(Math.sqrt(dx * dx + dy * dy));
      }
      const totalDistance = distances.reduce((sum, d) => sum + d, 0);

      // Calculate times based on distance (proportional timing)
      const times: number[] = [0];
      let cumulativeDistance = 0;
      for (const distance of distances) {
        cumulativeDistance += distance;
        times.push(cumulativeDistance / totalDistance);
      }

      // Animate through all positions smoothly using keyframes
      const keyframes = {
        left: allPositions.map(p => `${p.x}%`),
        top: allPositions.map(p => `${p.y}%`),
      };

      await dblb2Controls.start({
        left: keyframes.left,
        top: keyframes.top,
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });

      // Update position callback with final position
      if (onDBLB2PositionUpdate && reaction.response.length > 0) {
        onDBLB2PositionUpdate(reaction.response[reaction.response.length - 1]);
      }
    },
    [dblb2Controls, onDBLB2PositionUpdate]
  );

  /**
   * Animate DB/LB2 through a resolved, perception-based trajectory (uniform timing).
   * Same model as createDBLB1TrajectoryAnimation; DB/LB2 keys WR3 and runs the underneath
   * "Wall-Read on #2" read in Cover 4 Match (mirrored across midfield).
   */
  const createDBLB2TrajectoryAnimation = useCallback(
    async (positions: Position[], horizonMs: number, speed: number = 1): Promise<void> => {
      if (positions.length < 2) return;
      const totalDuration = horizonMs / 1000 / speed;
      const n = positions.length;
      const times = positions.map((_, i) => i / (n - 1));
      await dblb2Controls.start({
        left: positions.map((p) => `${p.x}%`),
        top: positions.map((p) => `${p.y}%`),
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });
      if (onDBLB2PositionUpdate) {
        onDBLB2PositionUpdate(positions[n - 1]);
      }
    },
    [dblb2Controls, onDBLB2PositionUpdate]
  );

  /**
   * Create SS reaction animation based on coverage rules
   */
  const createSSAnimation = useCallback(
    async (reaction: SSReaction, routeDuration: number, startPosition: Position, speed: number = 1): Promise<void> => {
      // Include start position as first point for smooth animation
      const allPositions = [startPosition, ...reaction.response];
      
      if (allPositions.length < 2) return;

      // Calculate total animation time (adjusted for playback speed)
      const totalDuration = routeDuration / 1000 / speed;

      // Calculate distances between each waypoint
      const distances: number[] = [];
      for (let i = 1; i < allPositions.length; i++) {
        const dx = allPositions[i].x - allPositions[i - 1].x;
        const dy = allPositions[i].y - allPositions[i - 1].y;
        distances.push(Math.sqrt(dx * dx + dy * dy));
      }
      const totalDistance = distances.reduce((sum, d) => sum + d, 0);

      // Calculate times based on distance (proportional timing)
      const times: number[] = [0];
      let cumulativeDistance = 0;
      for (const distance of distances) {
        cumulativeDistance += distance;
        times.push(cumulativeDistance / totalDistance);
      }

      // Animate through all positions smoothly using keyframes
      const keyframes = {
        left: allPositions.map(p => `${p.x}%`),
        top: allPositions.map(p => `${p.y}%`),
      };

      await ssControls.start({
        left: keyframes.left,
        top: keyframes.top,
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });

      // Update position callback with final position
      if (onSSPositionUpdate && reaction.response.length > 0) {
        onSSPositionUpdate(reaction.response[reaction.response.length - 1]);
      }
    },
    [ssControls, onSSPositionUpdate]
  );

  /**
   * Create LB reaction animation based on coverage rules
   */
  const createLBAnimation = useCallback(
    async (reaction: LBReaction, routeDuration: number, startPosition: Position, speed: number = 1): Promise<void> => {
      // Include start position as first point for smooth animation
      const allPositions = [startPosition, ...reaction.response];
      
      if (allPositions.length < 2) return;

      // Calculate total animation time (adjusted for playback speed)
      const totalDuration = routeDuration / 1000 / speed;

      // Calculate distances between each waypoint
      const distances: number[] = [];
      for (let i = 1; i < allPositions.length; i++) {
        const dx = allPositions[i].x - allPositions[i - 1].x;
        const dy = allPositions[i].y - allPositions[i - 1].y;
        distances.push(Math.sqrt(dx * dx + dy * dy));
      }
      const totalDistance = distances.reduce((sum, d) => sum + d, 0);

      // Calculate times based on distance (proportional timing)
      const times: number[] = [0];
      let cumulativeDistance = 0;
      for (const distance of distances) {
        cumulativeDistance += distance;
        times.push(cumulativeDistance / totalDistance);
      }

      // Animate through all positions smoothly using keyframes
      const keyframes = {
        left: allPositions.map(p => `${p.x}%`),
        top: allPositions.map(p => `${p.y}%`),
      };

      await lbControls.start({
        left: keyframes.left,
        top: keyframes.top,
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });

      // Update position callback with final position
      if (onLBPositionUpdate && reaction.response.length > 0) {
        onLBPositionUpdate(reaction.response[reaction.response.length - 1]);
      }
    },
    [lbControls, onLBPositionUpdate]
  );

  /**
   * Animate the LB through a resolved, perception-based trajectory (uniform timing).
   * Same model as createCB1TrajectoryAnimation; the LB keys the RB and runs the "Play RB"
   * hook read in Cover 4 Match.
   */
  const createLBTrajectoryAnimation = useCallback(
    async (positions: Position[], horizonMs: number, speed: number = 1): Promise<void> => {
      if (positions.length < 2) return;
      const totalDuration = horizonMs / 1000 / speed;
      const n = positions.length;
      const times = positions.map((_, i) => i / (n - 1));
      await lbControls.start({
        left: positions.map((p) => `${p.x}%`),
        top: positions.map((p) => `${p.y}%`),
        transition: {
          duration: totalDuration,
          ease: 'linear',
          times,
        },
      });
      if (onLBPositionUpdate) {
        onLBPositionUpdate(positions[n - 1]);
      }
    },
    [lbControls, onLBPositionUpdate]
  );

  /**
   * Execute the full simulation animation
   * Integrates route rules (Req 6.3) and coverage rules (Req 7.4)
   */
  const executeSimulation = useCallback(async () => {
    if (animationRef.current) return;
    animationRef.current = true;

    try {
      // Load and validate route data for WR1
      const routeData = loadRouteData(selectedRoute);
      if (!routeData) {
        console.error('Failed to load route data, aborting simulation');
        onAnimationComplete();
        return;
      }

      // Load and validate route data for WR2
      const routeData2 = loadRouteData(selectedRoute2);
      if (!routeData2) {
        console.error('Failed to load route2 data, aborting simulation');
        onAnimationComplete();
        return;
      }

      // Load and validate route data for WR3 and WR4 early (needed for DB/LB2 logic)
      const routeData3 = loadRouteData(selectedRoute3);
      const routeData4 = loadRouteData(selectedRoute4);
      if (!routeData3 || !routeData4) {
        console.error('Failed to load route3/4 data, aborting simulation');
        onAnimationComplete();
        return;
      }

      const { waypoints, duration: routeDuration } = routeData;
      const { waypoints: waypoints2, duration: routeDuration2 } = routeData2;
      const { waypoints: waypoints3, duration: routeDuration3 } = routeData3;
      const { waypoints: waypoints4, duration: routeDuration4 } = routeData4;

      // Load RB route data early (needed for DB/LB matching logic in Cover 4 Palms)
      const rbRouteData = loadRBRouteData(selectedRBRoute);
      if (!rbRouteData) {
        console.error('Failed to load RB route data, aborting simulation');
        onAnimationComplete();
        return;
      }
      const { waypoints: rbWaypoints, duration: rbRouteDuration } = rbRouteData;

      // Create offset waypoints for WR2 (shift x by 10, y by 5) and mirrored waypoints for
      // WR3/WR4, in REAL field coordinates. Computed early (moved up from further below,
      // where they're also still used for createWRAnimation/perception feeds) so that the
      // custom-defense per-position resolver — which needs each keyed receiver's REAL path —
      // can be invoked at the same point the legacy code picks each position's start/reaction,
      // without changing what these waypoint values are.
      const offsetWaypoints2 = waypoints2.map(wp => ({
        ...wp,
        position: { x: wp.position.x + 10, y: wp.position.y + 5 }
      }));
      const mirroredWaypoints3 = selectedFormation === 'trips'
        ? waypoints3.map(wp => ({
            ...wp,
            position: { x: wp.position.x + 20, y: wp.position.y + 5 }
          }))
        : waypoints3.map(wp => ({
            ...wp,
            position: { x: 100 - wp.position.x - 10, y: wp.position.y + 5 }
          }));
      const mirroredWaypoints4 = waypoints4.map(wp => ({
        ...wp,
        position: { x: 100 - wp.position.x, y: wp.position.y }
      }));

      // Shared horizon for the perception-based Cover 3 Match reads (doubles). A single horizon
      // keeps every position's feeds aligned index-for-index and the whole play the same length.
      // The RB route is included so a defender keying/matching the RB (apex Push, hook, weak apex)
      // sees the WHOLE route — a long wheel (3420ms) must not be truncated to a shorter WR horizon,
      // or the defender would stop reading right as the back turns up the field.
      const cover3HorizonMs = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4, rbRouteDuration, MIN_SIM_HORIZON_MS);
      const isCover3MatchDoubles = selectedCoverage === 'cover3Match' && selectedFormation !== 'trips';
      const isCover3MatchTrips = selectedCoverage === 'cover3Match' && selectedFormation === 'trips';
      // Cover 6 = Cover 4 (left) + Cover 2 (right). Doubles and trips share most reads; only DB/LB2
      // differs (Cover 2 apex vs single-side crosser). Reuses the shared cover3HorizonMs.
      const isCover6Doubles = selectedCoverage === 'cover6' && selectedFormation !== 'trips';
      const isCover6Trips = selectedCoverage === 'cover6' && selectedFormation === 'trips';
      const isCover6 = isCover6Doubles || isCover6Trips;
      // Cover 4 Palms = Cover 4 Match with a Palms twist on the corners (jump #2's early out).
      // Doubles: both corners Palms, both apexes wall #2, both safeties robber, LB hammer. Trips:
      // strong (left) side mirrors 2x2 Palms; weak (right) side = CB2 man / SS poach / DB/LB2 apex.
      const isCover4PalmsDoubles = selectedCoverage === 'cover4Palms' && selectedFormation !== 'trips';
      const isCover4PalmsTrips = selectedCoverage === 'cover4Palms' && selectedFormation === 'trips';
      const isCover4Palms = isCover4PalmsDoubles || isCover4PalmsTrips;

      /**
       * Dev-overlay plumbing for the BUILT-IN coverages.
       *
       * The custom-defense path funnels every position through `resolvePositionMovement`, which
       * returns an object carrying both the trajectory and the decision trace. The built-in
       * coverages below instead call the resolvers inline and keep only the path, so the reasoning
       * was computed and then dropped.
       *
       * `capture` fixes that without restructuring: each call site swaps its resolver for the
       * `Detailed` twin and wraps it, which files the trace here and hands back the same
       * `Position[]` the site already expected. Assignment shape, variable types and every read
       * site stay exactly as they were.
       *
       * `resolveDeepZone` has no `Detailed` twin — it is a spot-drop with no key feed to read, so
       * there is no decision to trace. Those positions are simply absent from the overlay.
       */
      const builtInTraces: Partial<Record<DefensePositionId, ReadTraceEntry[]>> = {};
      const capture = (position: DefensePositionId, result: ManMatchResult): Position[] => {
        builtInTraces[position] = result.trace;
        return result.trajectory;
      };

      // Load and validate coverage data from coverage rules system (Req 7.4)
      const coverageData = loadCoverageData(selectedCoverage, selectedRoute);
      if (!coverageData) {
        console.error('Failed to load coverage data, aborting simulation');
        onAnimationComplete();
        return;
      }

      let { startPosition: cb1Start, reaction: cb1Reaction } = coverageData;
      let cb1RouteDuration = routeDuration;

      // Custom-defense per-position resolution for CB1 (Req 4.1-4.5, 5.1-5.2). Only computed
      // when customDefenseAssignments is present; always resolved against selectedFormation
      // (the live Simulator formation), never a defense's saved formation.
      const cb1Movement: PositionMovementResolution | null = customDefenseAssignments
        ? resolvePositionMovement('cb1', customDefenseAssignments.cb1, selectedFormation, {
            keyRoute: selectedRoute,
            keyWaypoints: waypoints,
            keyRouteDuration: routeDuration,
            // Cover 4 Palms corner reads #2 (WR2) too, to jump an early "Out" call.
            crosserWaypoints: [waypoints, offsetWaypoints2, mirroredWaypoints3, mirroredWaypoints4],
          })
        : null;

      // Perception-based CB1 (simulate-then-animate). CB1 keys WR1 and reacts to what he
      // sees — WR1's positions over time — with no knowledge of the route called.
      let cb1ResolvedTrajectory: Position[] | null = null;
      let cb1SimHorizonMs = 0;
      if (selectedCoverage === 'cover4Match') {
        cb1SimHorizonMs = Math.max(routeDuration, MIN_SIM_HORIZON_MS);
        const wr1Feed = buildObservationFeed(waypoints, cb1SimHorizonMs);
        cb1ResolvedTrajectory = capture('cb1', resolveManMatchDetailed(cb1Start, wr1Feed));
      } else if (isCover3MatchDoubles || isCover3MatchTrips) {
        // CB1 plays the deep left third with a MOD read on WR1 (man vertical, else drop) — same
        // read in trips (WR1 is the strong-side #1).
        cb1SimHorizonMs = cover3HorizonMs;
        const wr1Feed = buildObservationFeed(waypoints, cb1SimHorizonMs);
        cb1ResolvedTrajectory = capture('cb1', resolveManMatchDetailed(cb1Start, wr1Feed, DEFAULT_MAN_MATCH_PARAMS_CB1_COVER3));
      } else if (isCover6) {
        // Cover 6: CB1 is the Cover 4 (left) corner — MOD read on WR1 (man vertical, else deep
        // left quarter). Same in trips (Palms on #1).
        cb1SimHorizonMs = cover3HorizonMs;
        const wr1Feed = buildObservationFeed(waypoints, cb1SimHorizonMs);
        cb1ResolvedTrajectory = capture('cb1', resolveManMatchDetailed(cb1Start, wr1Feed));
      } else if (isCover4Palms) {
        // Cover 4 Palms: CB1 plays Palms on WR1 — MOD man-match on #1, unless WR2 runs an early
        // short out (the "Out" call), when he jumps WR2 to the left flat. Same in trips (strong side).
        cb1SimHorizonMs = cover3HorizonMs;
        const wr1Feed = buildObservationFeed(waypoints, cb1SimHorizonMs);
        const wr2Feed = buildObservationFeed(offsetWaypoints2, cb1SimHorizonMs);
        cb1ResolvedTrajectory = capture('cb1', resolvePalmsCornerDetailed(cb1Start, wr1Feed, wr2Feed, DEFAULT_PALMS_CORNER_PARAMS_CB1));
      }

      if (selectedCoverage === 'cover3') {
        const maxDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
        // Cover 3 zone: CB1 follows whoever enters the deep left third
        const leftThirdDeepRoutes = ['go', 'corner', 'deepOut', 'comeback'];
        const wr1StaysLeft = leftThirdDeepRoutes.includes(selectedRoute);
        const wr2StaysLeft = leftThirdDeepRoutes.includes(selectedRoute2);

        if (wr1StaysLeft) {
          // WR1 enters deep left third — CB1 follows WR1
          cb1Reaction = getCoverageReaction(selectedCoverage, selectedRoute, selectedFormation);
          cb1RouteDuration = routeDuration;
        } else if (wr2StaysLeft) {
          // WR1 leaves but WR2 enters deep left third — CB1 follows WR2
          cb1Reaction = getCoverageReaction(selectedCoverage, selectedRoute2, selectedFormation);
          cb1RouteDuration = routeDuration2;
        } else if (selectedRoute === 'post' || selectedRoute === 'deepIn') {
          // WR1 crosses face to middle — follow briefly then hand off
          cb1Reaction = getCoverageReaction(selectedCoverage, selectedRoute, selectedFormation);
          cb1RouteDuration = routeDuration;
        } else if (selectedRoute2 === 'post' || selectedRoute2 === 'deepIn') {
          // WR2 crosses face to middle — follow briefly then hand off
          cb1Reaction = getCoverageReaction(selectedCoverage, selectedRoute2, selectedFormation);
          cb1RouteDuration = routeDuration2;
        } else {
          // No deep threat in left third — zone drop
          cb1Reaction = getCoverageReaction(selectedCoverage, 'zone_drop', selectedFormation);
          cb1RouteDuration = maxDuration;
        }
      } else if (selectedCoverage === 'cover3Match' && ['slant', 'out', 'drag', 'hitch'].includes(selectedRoute)) {
        // Cover 3 Match CB1 zone drop: when no match condition is met (short routes),
        // CB1 drops to deep third at the same pace as FS by using the longest route duration
        const maxDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
        cb1RouteDuration = maxDuration;
      }

      // Cover 4 Palms CB1 logic:
      // 1. If WR2 runs short out → CB1 takes WR2
      // 2. Otherwise CB1 reads WR1 (deep routes = match, short routes = zone drop)
      if (selectedCoverage === 'cover4Palms') {
        if (selectedRoute2 === 'out') {
          // WR2 runs short out - CB1 takes WR2
          cb1Reaction = getCoverageReaction(selectedCoverage, 'out_wr2', selectedFormation);
          cb1RouteDuration = routeDuration2;
        }
        // Otherwise use default WR1 reaction (already loaded)
      }

      // Cover 6 trips CB1 logic (3 WR side plays Palms principles):
      // 1. If WR2 runs short out → CB1 makes "Out" call, covers WR2
      // 2. Otherwise CB1 reads WR1 (deep routes = match, short routes = zone drop)
      if (selectedCoverage === 'cover6' && selectedFormation === 'trips') {
        if (selectedRoute2 === 'out') {
          // WR2 runs short out - CB1 makes "Out" call, takes WR2
          cb1Reaction = getCoverageReaction(selectedCoverage, 'out_wr2', selectedFormation);
          cb1RouteDuration = routeDuration2;
        }
        // Otherwise use default WR1 reaction (already loaded)
      }

      // Get DB/LB1 data
      // In cover4Match: DB/LB1 covers whichever WR (WR1 or WR2) runs a short out
      // In cover4Palms: CB1 takes WR2 on short out, so DB/LB1 zones (uses 'out' reaction which is zone drop)
      // In cover3Match: DB/LB1 matches WR2 unless WR2 goes inside (slant), then picks up RB if RB comes to left side
      // Otherwise defaults to covering WR2
      const dblb1Start = getDBLB1StartPosition();
      let dblb1Reaction = getDBLB1Reaction(selectedCoverage, selectedRoute2, selectedFormation);
      let dblb1RouteDuration = routeDuration2;

      const dblb1Movement: PositionMovementResolution | null = customDefenseAssignments
        ? resolvePositionMovement('dblb1', customDefenseAssignments.dblb1, selectedFormation, {
            keyRoute: selectedRoute2,
            keyWaypoints: offsetWaypoints2,
            keyRouteDuration: routeDuration2,
            // Cover 3 apex reads WR1 (the #1) and the RB as secondary keys; crossers ([WR1..WR4])
            // give the trips apex access to WR3 (its fast-to-flat threat).
            partnerWaypoints: waypoints,
            rbWaypoints: rbWaypoints,
            crosserWaypoints: [waypoints, offsetWaypoints2, mirroredWaypoints3, mirroredWaypoints4],
          })
        : null;

      // Perception-based DB/LB1 (cover4Match): underneath "Wall-Read on #2". DB/LB1 keys WR2
      // and reacts to what he sees — WR2's REAL (offset) positions over time — jumping a short
      // out/hitch man-to-man, otherwise passing #2 off and dropping to the curl-flat.
      let dblb1ResolvedTrajectory: Position[] | null = null;
      let dblb1SimHorizonMs = 0;
      if (selectedCoverage === 'cover4Match') {
        // Include the RB route in the horizon so the apex can carry a wheeling RB the whole way
        // on a Push (fast/wide release to his flat), not just until a shorter WR horizon runs out.
        dblb1SimHorizonMs = Math.max(routeDuration2, rbRouteDuration, MIN_SIM_HORIZON_MS);
        const wr2Feed = buildObservationFeed(offsetWaypoints2, dblb1SimHorizonMs);
        const rbFeed = buildObservationFeed(rbWaypoints, dblb1SimHorizonMs);
        // WR1 (#1) feed: the apex comes off #2 to cover a #1 short in-breaker when no one's in the flat.
        const wr1Feed = buildObservationFeed(waypoints, dblb1SimHorizonMs);
        dblb1ResolvedTrajectory = capture('dblb1', resolveUnderneathMatchDetailed(dblb1Start, wr2Feed, DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1, rbFeed, wr1Feed));
      } else if (isCover3MatchDoubles) {
        // Apex "match #2" on WR2, reading WR1 (short out/curl → rob) and the RB (fast to left flat).
        dblb1SimHorizonMs = cover3HorizonMs;
        const wr2Feed = buildObservationFeed(offsetWaypoints2, dblb1SimHorizonMs);
        const wr1Feed = buildObservationFeed(waypoints, dblb1SimHorizonMs);
        const rbFeed = buildObservationFeed(rbWaypoints, dblb1SimHorizonMs);
        dblb1ResolvedTrajectory = capture('dblb1', resolveApexMatchDetailed(dblb1Start, wr2Feed, DEFAULT_APEX_MATCH_PARAMS_DBLB1, wr1Feed, rbFeed));
      } else if (isCover3MatchTrips) {
        // Trips apex "match #2" on WR2, reading WR1 (short out/curl → rob) and WR3 (the #3) as the
        // fast-to-strong-flat threat instead of the RB.
        dblb1SimHorizonMs = cover3HorizonMs;
        const wr2Feed = buildObservationFeed(offsetWaypoints2, dblb1SimHorizonMs);
        const wr1Feed = buildObservationFeed(waypoints, dblb1SimHorizonMs);
        const wr3Feed = buildObservationFeed(mirroredWaypoints3, dblb1SimHorizonMs);
        dblb1ResolvedTrajectory = capture('dblb1', resolveApexMatchDetailed(dblb1Start, wr2Feed, DEFAULT_APEX_MATCH_PARAMS_DBLB1_TRIPS, wr1Feed, wr3Feed));
      } else if (isCover6) {
        // Cover 6: DB/LB1 is the Cover 4 (left) apex — walls WR2 (#2). In doubles the Cover 4 side
        // is standard Cover 4 Match (jumps a #2 short out); in trips the 3-WR side plays Palms, so
        // a #2 short out is passed off to the Palms corner ("Out" call) instead of jumped.
        dblb1SimHorizonMs = cover3HorizonMs;
        const wr2Feed = buildObservationFeed(offsetWaypoints2, dblb1SimHorizonMs);
        dblb1ResolvedTrajectory = capture('dblb1', resolveUnderneathMatchDetailed(
          dblb1Start,
          wr2Feed,
          isCover6Trips ? DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1_PALMS : DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1
        ));
      } else if (isCover4Palms) {
        // Cover 4 Palms: DB/LB1 is the (strong-side) apex — "Wall-Read on #2" (WR2). Like Cover 4
        // Match, but a #2 short out is passed off (the Palms corner takes it on the "Out" call), so
        // the apex sinks to the flat/curl instead of jumping the out. Reads WR1 (#1) so it can come
        // off a passed-off #2 to cover a #1 short in-breaker with no one in the flat. Same in trips.
        dblb1SimHorizonMs = cover3HorizonMs;
        const wr2Feed = buildObservationFeed(offsetWaypoints2, dblb1SimHorizonMs);
        const rbFeed = buildObservationFeed(rbWaypoints, dblb1SimHorizonMs);
        const wr1Feed = buildObservationFeed(waypoints, dblb1SimHorizonMs);
        dblb1ResolvedTrajectory = capture('dblb1', resolveUnderneathMatchDetailed(dblb1Start, wr2Feed, DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1_PALMS, rbFeed, wr1Feed));
      }

      if (selectedCoverage === 'cover3Match') {
        // DB/LB1 matches WR2 on vertical routes and short out
        // Lets WR2 go on slant (5yd or less inside)
        // DB/LB1 does NOT pick up RB curl - that's DB/LB2's job since curl ends on right side
        // Default reaction from selectedRoute2 handles this
      } else if (selectedCoverage === 'cover3') {
        // Cover 3 zone: DB/LB1 drops to left flat zone — use max duration for natural pace
        dblb1Reaction = getDBLB1Reaction(selectedCoverage, selectedRoute2, selectedFormation);
        dblb1RouteDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
      } else if (selectedCoverage === 'cover4Palms') {
        // In Cover 4 Palms: CB1 takes WR2 on short out, DB/LB1 zones
        // WR1 short out - DB/LB1 follows WR1
        if (selectedRoute === 'out') {
          dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'out_wr1', selectedFormation);
          dblb1RouteDuration = routeDuration;
        } else {
          // Check if RB is on the opposite side (right side, x > 50)
          // DB/LB1 is on left side, so if RB is on right side, DB/LB1 matches in-breaking routes
          const rbStartX = rbWaypoints[0].position.x;
          const rbOnRightSide = rbStartX > 50;
          
          if (rbOnRightSide) {
            if (selectedRBRoute === 'curl') {
              // RB is on opposite side and runs curl (in-breaking) - DB/LB1 matches RB
              dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'curl_rb', selectedFormation);
              dblb1RouteDuration = loadRBRouteData(selectedRBRoute)?.duration || 800;
            } else if (isInBreakingRoute(selectedRoute)) {
              // RB is on opposite side, WR1 runs in-breaking route - DB/LB1 follows WR1
              dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'slant', selectedFormation);
              dblb1RouteDuration = routeDuration;
            } else if (isInBreakingRoute(selectedRoute2)) {
              // RB is on opposite side, WR2 runs in-breaking route - DB/LB1 follows WR2
              dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'slant_wr2', selectedFormation);
              dblb1RouteDuration = routeDuration2;
            }
          }
        }
        // WR2 short out - CB1 takes him, DB/LB1 uses default 'out' reaction (zone drop)
        // Other routes - use default WR2 reaction
      } else if (selectedCoverage === 'cover6' && selectedFormation !== 'trips') {
        // Cover 6 2x2: Left side plays Cover 4 Match principles.
        // CB1 drops any WR1 route under 10 yards to the deep quarter (no man-call framing).
        // Apex breaks to cover WR1 when WR1 works short out or hitch into his area.
        if (selectedRoute === 'out') {
          dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'out_wr1', selectedFormation);
          dblb1RouteDuration = routeDuration;
        } else if (selectedRoute === 'hitch') {
          dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'hitch_wr1', selectedFormation);
          dblb1RouteDuration = routeDuration;
        }
        // Otherwise use default WR2 reaction
      } else if (selectedCoverage === 'cover6' && selectedFormation === 'trips') {
        // Cover 6 trips: 3 WR side plays Palms principles
        // - WR1 short in-breaker (slant/drag), no one in flat → DB/LB1 covers WR1 across the field
        //   (this takes priority — even when WR2 runs out, DB/LB1 chases WR1 since CB1 has WR2)
        // - WR1 hitch → DB/LB1 immediately breaks to cover WR1
        // - WR2 short out alone → DB/LB1 plays flat (CB1 has WR2, no #1 underneath)
        if (selectedRoute === 'slant') {
          // WR1 slant — short in, no one in flat. DB/LB1 covers WR1 across the field.
          dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'slant', selectedFormation);
          dblb1RouteDuration = routeDuration;
        } else if (selectedRoute === 'drag') {
          // WR1 drag — short in, no one in flat. DB/LB1 covers WR1 across the field.
          dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'drag', selectedFormation);
          dblb1RouteDuration = routeDuration;
        } else if (selectedRoute === 'hitch') {
          // WR1 hitch — DB/LB1 breaks to cover WR1.
          dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'hitch_wr1', selectedFormation);
          dblb1RouteDuration = routeDuration;
        } else if (selectedRoute2 === 'out') {
          // WR2 short out (no #1 underneath) — CB1 has WR2. DB/LB1 plays flat.
          dblb1Reaction = getDBLB1Reaction(selectedCoverage, 'out_wr2', selectedFormation);
          dblb1RouteDuration = routeDuration2;
        }
        // Otherwise use default WR2 reaction
      }

      // Get FS data - uses WR2's route since FS is responsible for deep zone over WR2
      // In cover4Palms: If WR2 runs short out (CB1 takes him), FS matches WR1 on deep routes
      const fsStart = getFSStartPosition();
      let fsReaction = getFSReaction(selectedCoverage, selectedRoute2, selectedFormation);
      let fsRouteDuration = routeDuration2;

      const fsMovement: PositionMovementResolution | null = customDefenseAssignments
        ? resolvePositionMovement('fs', customDefenseAssignments.fs, selectedFormation, {
            keyRoute: selectedRoute2,
            keyWaypoints: offsetWaypoints2,
            keyRouteDuration: routeDuration2,
            partnerRoute: selectedRoute,
            partnerWaypoints: waypoints,
            partnerRouteDuration: routeDuration,
          })
        : null;

      // Cover 3 zone FS: follow WRs that enter the deep middle third (posts/deep ins)
      if (selectedCoverage === 'cover3') {
        const maxDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
        // Routes that enter the middle third from the left side
        const leftToMiddle = (selectedRoute === 'post' || selectedRoute === 'deepIn')
          || (selectedRoute2 === 'post' || selectedRoute2 === 'deepIn');
        // Routes that enter the middle third from the right side
        const rightToMiddle = (selectedRoute3 === 'post' || selectedRoute3 === 'deepIn')
          || (selectedRoute4 === 'post' || selectedRoute4 === 'deepIn');

        if (leftToMiddle && rightToMiddle) {
          // Both sides have WRs entering middle — follow the post (stays in zone) over deepIn (passes through)
          const leftHasPost = (selectedRoute === 'post') || (selectedRoute2 === 'post');
          const rightHasPost = (selectedRoute3 === 'post') || (selectedRoute4 === 'post');

          if (leftHasPost && rightHasPost) {
            // Posts from both sides — stay centered deep
            fsReaction = getFSReaction(selectedCoverage, 'middle_both', selectedFormation);
            fsRouteDuration = maxDuration;
          } else if (leftHasPost) {
            // Post from left is the bigger threat — follow it
            const isWr2 = selectedRoute2 === 'post';
            const wrKey = isWr2 ? 'wr2' : 'wr1';
            fsReaction = getFSReaction(selectedCoverage, `post_${wrKey}`, selectedFormation);
            fsRouteDuration = isWr2 ? routeDuration2 : routeDuration;
          } else if (rightHasPost) {
            // Post from right is the bigger threat — follow it
            const isWr3 = selectedRoute3 === 'post';
            const wrKey = isWr3 ? 'wr3' : 'wr4';
            fsReaction = getFSReaction(selectedCoverage, `post_${wrKey}`, selectedFormation);
            fsRouteDuration = isWr3 ? routeDuration3 : routeDuration4;
          } else {
            // Both sides running deepIn — stay centered deep
            fsReaction = getFSReaction(selectedCoverage, 'middle_both', selectedFormation);
            fsRouteDuration = maxDuration;
          }
        } else if (leftToMiddle) {
          // WR from left enters middle — prefer WR2 (inner) since it enters middle more directly
          const isWr2 = selectedRoute2 === 'post' || selectedRoute2 === 'deepIn';
          const wrKey = isWr2 ? 'wr2' : 'wr1';
          const leftRoute = isWr2 ? selectedRoute2 : selectedRoute;
          fsReaction = getFSReaction(selectedCoverage, `${leftRoute}_${wrKey}`, selectedFormation);
          fsRouteDuration = isWr2 ? routeDuration2 : routeDuration;
        } else if (rightToMiddle) {
          // WR from right enters middle — prefer WR3 (inner) since it enters middle more directly
          const isWr3 = selectedRoute3 === 'post' || selectedRoute3 === 'deepIn';
          const wrKey = isWr3 ? 'wr3' : 'wr4';
          const rightRoute = isWr3 ? selectedRoute3 : selectedRoute4;
          fsReaction = getFSReaction(selectedCoverage, `${rightRoute}_${wrKey}`, selectedFormation);
          fsRouteDuration = isWr3 ? routeDuration3 : routeDuration4;
        } else {
          // No WR entering middle — zone drop
          fsReaction = getFSReaction(selectedCoverage, 'zone_drop', selectedFormation);
          fsRouteDuration = maxDuration;
        }
      } else if (selectedCoverage === 'cover3Match') {
        // Cover 3 Match FS: use longest route duration so FS drops at a natural pace
        const maxDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
        fsRouteDuration = maxDuration;
      }

      // Deep routes are 10+ yards: deepOut, deepIn, comeback, go, post, corner
      const deepRoutes = ['deepOut', 'deepIn', 'comeback', 'go', 'post', 'corner'];

      if (selectedCoverage === 'cover4Palms') {
        if (selectedRoute2 === 'out' && deepRoutes.includes(selectedRoute)) {
          // WR2 runs short out (CB1 takes him), WR1 goes deep - FS matches WR1
          fsReaction = getFSReaction(selectedCoverage, selectedRoute, selectedFormation);
          fsRouteDuration = routeDuration;
        }
        // Otherwise FS uses default WR2 reaction
      } else if (selectedCoverage === 'cover6' && selectedFormation === 'trips') {
        if (selectedRoute2 === 'out' && deepRoutes.includes(selectedRoute)) {
          // Cover 6 trips Palms: WR2 out → "Out" call → FS takes WR1 man-to-man
          fsReaction = getFSReaction(selectedCoverage, selectedRoute, selectedFormation);
          fsRouteDuration = routeDuration;
        }
        // Otherwise FS uses default WR2 reaction (Robber-Read)
      } else if (selectedCoverage === 'man') {
        // Man coverage: FS provides deep help on left side (WR1 + WR2)
        // If multiple deep threats, stay centered — don't commit to one
        const wr1Deep = deepRoutes.includes(selectedRoute);
        const wr2Deep = deepRoutes.includes(selectedRoute2);
        if (wr1Deep && wr2Deep) {
          fsReaction = getFSReaction(selectedCoverage, 'deep_help', selectedFormation);
          fsRouteDuration = Math.max(routeDuration, routeDuration2);
        } else if (wr1Deep) {
          fsReaction = getFSReaction(selectedCoverage, selectedRoute, selectedFormation);
          fsRouteDuration = routeDuration;
        } else if (wr2Deep) {
          fsReaction = getFSReaction(selectedCoverage, selectedRoute2, selectedFormation);
          fsRouteDuration = routeDuration2;
        }
        // If neither is deep, default WR2 reaction (moderate drop)
      }

      // Get CB2 data - uses WR4's route since CB2 covers WR4
      const cb2Start = getCB2StartPosition();
      let cb2Reaction = getCB2Reaction(selectedCoverage, selectedRoute4, selectedFormation);
      let cb2RouteDuration = routeDuration4;

      const cb2Movement: PositionMovementResolution | null = customDefenseAssignments
        ? resolvePositionMovement('cb2', customDefenseAssignments.cb2, selectedFormation, {
            keyRoute: selectedRoute4,
            keyWaypoints: mirroredWaypoints4,
            keyRouteDuration: routeDuration4,
            // Cover 4 Palms corner reads #2 (WR3) too, to jump an early "Out" call.
            crosserWaypoints: [waypoints, offsetWaypoints2, mirroredWaypoints3, mirroredWaypoints4],
          })
        : null;

      if (selectedCoverage === 'cover3') {
        const maxDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
        const rightThirdDeepRoutes = ['go', 'corner', 'deepOut', 'comeback'];
        const wr4StaysRight = rightThirdDeepRoutes.includes(selectedRoute4);
        const wr3StaysRight = rightThirdDeepRoutes.includes(selectedRoute3);

        if (wr4StaysRight) {
          // WR4 enters deep right third — CB2 follows WR4
          cb2Reaction = getCB2Reaction(selectedCoverage, selectedRoute4, selectedFormation);
          cb2RouteDuration = routeDuration4;
        } else if (wr3StaysRight) {
          // WR4 leaves but WR3 enters deep right third — CB2 follows WR3
          cb2Reaction = getCB2Reaction(selectedCoverage, selectedRoute3, selectedFormation);
          cb2RouteDuration = routeDuration3;
        } else if (selectedRoute4 === 'post' || selectedRoute4 === 'deepIn') {
          // WR4 crosses face to middle — follow briefly then hand off
          cb2Reaction = getCB2Reaction(selectedCoverage, selectedRoute4, selectedFormation);
          cb2RouteDuration = routeDuration4;
        } else if (selectedRoute3 === 'post' || selectedRoute3 === 'deepIn') {
          // WR3 crosses face to middle — follow briefly then hand off
          cb2Reaction = getCB2Reaction(selectedCoverage, selectedRoute3, selectedFormation);
          cb2RouteDuration = routeDuration3;
        } else {
          // No deep threat in right third — zone drop
          cb2Reaction = getCB2Reaction(selectedCoverage, 'zone_drop', selectedFormation);
          cb2RouteDuration = maxDuration;
        }
      } else if (selectedCoverage === 'cover3Match' && ['slant', 'out', 'drag', 'hitch'].includes(selectedRoute4)) {
        // Cover 3 Match CB2 zone drop
        const maxDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
        cb2RouteDuration = maxDuration;
      }

      // Cover 4 Palms CB2 logic:
      // 1. If WR3 runs short out → CB2 takes WR3
      // 2. Otherwise CB2 reads WR4 (deep routes = match, short routes = zone drop)
      if (selectedCoverage === 'cover4Palms') {
        if (selectedRoute3 === 'out') {
          // WR3 runs short out - CB2 takes WR3
          cb2Reaction = getCB2Reaction(selectedCoverage, 'out_wr3', selectedFormation);
          cb2RouteDuration = routeDuration3;
        }
        // Otherwise use default WR4 reaction (already loaded)
      } else if (selectedCoverage === 'cover6') {
        if (selectedFormation === 'trips') {
          // Cover 6 trips: Single WR side (right). CB2 plays straight man on WR4. No exceptions.
          cb2Reaction = getCB2Reaction(selectedCoverage, selectedRoute4, selectedFormation);
          cb2RouteDuration = routeDuration4;
        } else {
          // Cover 6 2x2: CB2 first looks for short out-breaking routes, if none match WR4
          if (selectedRoute3 === 'out') {
            cb2Reaction = getCB2Reaction(selectedCoverage, 'out_wr3', selectedFormation);
            cb2RouteDuration = routeDuration3;
          } else if (selectedRoute4 === 'out') {
            cb2Reaction = getCB2Reaction(selectedCoverage, 'out_wr4', selectedFormation);
            cb2RouteDuration = routeDuration4;
          } else {
            // No short out - match WR4 on whatever route
            cb2Reaction = getCB2Reaction(selectedCoverage, selectedRoute4, selectedFormation);
            cb2RouteDuration = routeDuration4;
          }
        }
      }

      // Get DB/LB2 data - uses WR3's route since DB/LB2 covers WR3
      // In cover4Match: DB/LB2 covers whichever WR (WR3 or WR4) runs a short out
      // In cover4Palms: CB2 takes WR3 on short out, so DB/LB2 zones (uses 'out' reaction which is zone drop)
      const dblb2Start = getDBLB2StartPosition();
      let dblb2Reaction = getDBLB2Reaction(selectedCoverage, selectedRoute3, selectedFormation);
      let dblb2RouteDuration = routeDuration3;

      const dblb2Movement: PositionMovementResolution | null = customDefenseAssignments
        ? resolvePositionMovement('dblb2', customDefenseAssignments.dblb2, selectedFormation, {
            keyRoute: selectedRoute3,
            keyWaypoints: mirroredWaypoints3,
            keyRouteDuration: routeDuration3,
            // #1 for the right apex is WR4 — used by the cover4Match wall-read's "#1 short
            // in-breaker" rule (reroute #2, cover #1).
            partnerWaypoints: mirroredWaypoints4,
            // Cover 3 uses DB/LB2 as the RIGHT inside hook: keys the RB + WR crossers.
            rbWaypoints: rbWaypoints,
            crosserWaypoints: [waypoints, offsetWaypoints2, mirroredWaypoints3, mirroredWaypoints4],
          })
        : null;

      // Perception-based DB/LB2 (cover4Match): underneath "Wall-Read on #2". DB/LB2 keys WR3
      // and reacts to what he sees — WR3's REAL (mirrored/offset) positions over time —
      // jumping a short out/hitch man-to-man, otherwise passing #2 off and dropping to the
      // right curl-flat. Mirror of the DB/LB1 perception block above.
      let dblb2ResolvedTrajectory: Position[] | null = null;
      let dblb2SimHorizonMs = 0;
      if (selectedCoverage === 'cover4Match' && selectedFormation === 'trips') {
        // Cover 4 Match trips: DB/LB2 is the WEAK apex (WR3 is a strong-side receiver in trips, so
        // he has no #2 to wall). He mans the RB on any release to the weak (right) side and carries
        // him everywhere, else matches the first strong-side crosser. Mirrors Cover 4 Palms trips.
        dblb2SimHorizonMs = cover3HorizonMs;
        const rbFeed = buildObservationFeed(rbWaypoints, dblb2SimHorizonMs);
        const crosserFeeds = [waypoints, offsetWaypoints2, mirroredWaypoints3].map((w) =>
          buildObservationFeed(w, dblb2SimHorizonMs)
        );
        dblb2ResolvedTrajectory = capture('dblb2', resolveHookReadDetailed(dblb2Start, rbFeed, DEFAULT_HOOK_READ_PARAMS_SS_TRIPS, crosserFeeds));
      } else if (selectedCoverage === 'cover4Match') {
        // Doubles: wall WR3 (#2), and take the RB on a Push (fast/wide release to the right flat).
        dblb2SimHorizonMs = Math.max(routeDuration3, rbRouteDuration, MIN_SIM_HORIZON_MS);
        const wr3Feed = buildObservationFeed(mirroredWaypoints3, dblb2SimHorizonMs);
        const rbFeed = buildObservationFeed(rbWaypoints, dblb2SimHorizonMs);
        // WR4 (#1 on the right) feed: come off #2 to cover a #1 short in-breaker with no one in the flat.
        const wr4Feed = buildObservationFeed(mirroredWaypoints4, dblb2SimHorizonMs);
        dblb2ResolvedTrajectory = capture('dblb2', resolveUnderneathMatchDetailed(dblb2Start, wr3Feed, DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2, rbFeed, wr4Feed));
      } else if (isCover3MatchDoubles) {
        // In Cover 3 Match, DB/LB2 is the RIGHT inside hook: play the RB if he releases to this
        // side (else let a fast flat go), otherwise look for crossers into the right hook.
        dblb2SimHorizonMs = cover3HorizonMs;
        const rbFeed = buildObservationFeed(rbWaypoints, dblb2SimHorizonMs);
        const crosserFeeds = [waypoints, offsetWaypoints2, mirroredWaypoints3, mirroredWaypoints4].map((w) =>
          buildObservationFeed(w, dblb2SimHorizonMs)
        );
        dblb2ResolvedTrajectory = capture('dblb2', resolveHookReadDetailed(dblb2Start, rbFeed, DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER3, crosserFeeds));
      } else if (isCover3MatchTrips) {
        // Trips MIDDLE hook: matches the WR3 crosser (the LB's pass-off) with priority, else covers
        // the RB only on a middle (central) release, else holds the hook.
        dblb2SimHorizonMs = cover3HorizonMs;
        const rbFeed = buildObservationFeed(rbWaypoints, dblb2SimHorizonMs);
        const wr3Feed = buildObservationFeed(mirroredWaypoints3, dblb2SimHorizonMs);
        dblb2ResolvedTrajectory = capture('dblb2', resolveHookReadDetailed(dblb2Start, rbFeed, DEFAULT_HOOK_READ_PARAMS_DBLB2_TRIPS, [wr3Feed]));
      } else if (isCover6Doubles) {
        // Cover 6 (2x2): DB/LB2 is the Cover 2 (right) apex — matches WR3 (#2), walling the vertical
        // (passed to the deep-half SS) and jumping a short out/hitch.
        dblb2SimHorizonMs = cover3HorizonMs;
        const wr3Feed = buildObservationFeed(mirroredWaypoints3, dblb2SimHorizonMs);
        dblb2ResolvedTrajectory = capture('dblb2', resolveUnderneathMatchDetailed(dblb2Start, wr3Feed, DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2));
      } else if (isCover6Trips) {
        // Cover 6 (trips): DB/LB2 is the single (right) side underneath defender — no RB (the LB
        // hammers #3), so he's a pure crosser matcher, taking the first route from the 3-WR side.
        dblb2SimHorizonMs = cover3HorizonMs;
        const rbFeed = buildObservationFeed(rbWaypoints, dblb2SimHorizonMs);
        const crosserFeeds = [waypoints, offsetWaypoints2, mirroredWaypoints3].map((w) =>
          buildObservationFeed(w, dblb2SimHorizonMs)
        );
        dblb2ResolvedTrajectory = capture('dblb2', resolveHookReadDetailed(dblb2Start, rbFeed, DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER6, crosserFeeds));
      } else if (isCover4PalmsDoubles) {
        // Cover 4 Palms (2x2): DB/LB2 is the right apex — "Wall-Read on #2" (WR3), passing off a
        // short out to the Palms corner. Mirror of DB/LB1. Also takes the RB on a Push (fast/wide
        // release to the right flat) and carries him. Reads WR4 (#1) for the "#1 short in-breaker".
        dblb2SimHorizonMs = cover3HorizonMs;
        const wr3Feed = buildObservationFeed(mirroredWaypoints3, dblb2SimHorizonMs);
        const rbFeed = buildObservationFeed(rbWaypoints, dblb2SimHorizonMs);
        const wr4Feed = buildObservationFeed(mirroredWaypoints4, dblb2SimHorizonMs);
        dblb2ResolvedTrajectory = capture('dblb2', resolveUnderneathMatchDetailed(dblb2Start, wr3Feed, DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2_PALMS, rbFeed, wr4Feed));
      } else if (isCover4PalmsTrips) {
        // Cover 4 Palms (trips): DB/LB2 is the WEAK apex — mans the RB on any release to the weak
        // (right) side, else matches the first crosser coming from the 3-WR (strong) side. Reuses
        // the Cover 3 trips weak-apex hook params.
        dblb2SimHorizonMs = cover3HorizonMs;
        const rbFeed = buildObservationFeed(rbWaypoints, dblb2SimHorizonMs);
        const crosserFeeds = [waypoints, offsetWaypoints2, mirroredWaypoints3].map((w) =>
          buildObservationFeed(w, dblb2SimHorizonMs)
        );
        dblb2ResolvedTrajectory = capture('dblb2', resolveHookReadDetailed(dblb2Start, rbFeed, DEFAULT_HOOK_READ_PARAMS_SS_TRIPS, crosserFeeds));
      }
      
      if (selectedCoverage === 'cover4Palms') {
        // In Cover 4 Palms: CB2 takes WR3 on short out, DB/LB2 zones
        // WR4 short out - DB/LB2 follows WR4
        if (selectedRoute4 === 'out') {
          dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'out_wr4', selectedFormation);
          dblb2RouteDuration = routeDuration4;
        } else {
          const rbStartX = rbWaypoints[0].position.x;
          const rbOnLeftSide = rbStartX <= 50;
          const rbOnRightSide = rbStartX > 50;
          
          if (rbOnLeftSide) {
            // RB is on opposite side - DB/LB2 matches in-breaking routes
            if (selectedRBRoute === 'curl') {
              // RB runs curl (in-breaking) - DB/LB2 matches RB
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'curl_rb', selectedFormation);
              dblb2RouteDuration = loadRBRouteData(selectedRBRoute)?.duration || 800;
            } else if (isInBreakingRoute(selectedRoute4)) {
              // WR4 runs in-breaking route - DB/LB2 follows WR4
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'slant_wr4', selectedFormation);
              dblb2RouteDuration = routeDuration4;
            }
          } else if (rbOnRightSide) {
            // RB is on DB/LB2's side - DB/LB2 matches RB on out-breaking routes
            if (selectedRBRoute === 'out') {
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'out_rb', selectedFormation);
              dblb2RouteDuration = loadRBRouteData(selectedRBRoute)?.duration || 1200;
            } else if (selectedRBRoute === 'swing') {
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'swing_rb', selectedFormation);
              dblb2RouteDuration = loadRBRouteData(selectedRBRoute)?.duration || 1000;
            }
          }
        }
        // WR3 short out - CB2 takes him, DB/LB2 uses default 'out' reaction (zone drop)
        // Other routes - use default WR3 reaction
      } else if (selectedCoverage === 'cover6') {
        if (selectedFormation === 'trips') {
          // Cover 6 trips: Single WR side Apex. Take crossers from 3 WR (left) side, otherwise zone.
          // Crossers from strong side = WR1, WR2, or WR3 running drag/slant toward weak side
          const wr1Crosser = isInBreakingRoute(selectedRoute);
          const wr2Crosser = isInBreakingRoute(selectedRoute2);
          const wr3Crosser = isInBreakingRoute(selectedRoute3);

          if (wr1Crosser || wr2Crosser || wr3Crosser) {
            // Pick the latest crosser entering DB/LB2's zone (closest to him)
            // WR3 starts closest (x=40), then WR2 (x=30), then WR1 (x=20)
            let crosserRouteType: string;
            let crosserWR: string;
            let duration: number;
            if (wr3Crosser) {
              crosserRouteType = selectedRoute3;
              crosserWR = 'wr3';
              duration = routeDuration3;
            } else if (wr2Crosser) {
              crosserRouteType = selectedRoute2;
              crosserWR = 'wr2';
              duration = routeDuration2;
            } else {
              crosserRouteType = selectedRoute;
              crosserWR = 'wr1';
              duration = routeDuration;
            }
            const triggerKey = `${crosserRouteType === 'drag' ? 'drag' : 'slant'}_${crosserWR}`;
            dblb2Reaction = getDBLB2Reaction(selectedCoverage, triggerKey, selectedFormation);
            dblb2RouteDuration = duration;
          } else {
            // No crosser — zone drop
            dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'zone_drop', selectedFormation);
            dblb2RouteDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
          }
        } else {
          // Cover 6 2x2: DB/LB2 matches WR3 unless WR3 goes short out
          // If WR3 short out, match WR4 if WR4 goes short in (slant), otherwise zone drop
          if (selectedRoute3 === 'out') {
            if (isInBreakingRoute(selectedRoute4)) {
              // WR3 short out + WR4 short in → match WR4
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'slant_wr4', selectedFormation);
              dblb2RouteDuration = routeDuration4;
            } else {
              // WR3 short out + WR4 not short in → zone drop
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'zone_drop', selectedFormation);
              dblb2RouteDuration = routeDuration3;
            }
          } else {
            // WR3 not short out → match WR3 on whatever route
            dblb2Reaction = getDBLB2Reaction(selectedCoverage, selectedRoute3, selectedFormation);
            dblb2RouteDuration = routeDuration3;
          }
        }
      } else if (selectedCoverage === 'cover3Match' || selectedCoverage === 'cover3') {
        // PROXIMITY-BASED: DB/LB2 matches whoever is in his zone (x > ~45)
        // Right side slants (WR3/WR4) start in his zone - match then release when they cross out
        // Left side slants (WR1/WR2) cross into his zone - pick them up
        // RB curl takes PRIORITY
        
        const rbCurlEndsRight = selectedRBRoute === 'curl';
        
        if (rbCurlEndsRight) {
          dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'curl_rb', selectedFormation);
          dblb2RouteDuration = loadRBRouteData(selectedRBRoute)?.duration || 800;
        } else {
          // Check for in-breaking routes (slant/drag)
          const hasRightSlant = isInBreakingRoute(selectedRoute3) || isInBreakingRoute(selectedRoute4);
          const hasLeftSlant = isInBreakingRoute(selectedRoute) || isInBreakingRoute(selectedRoute2);
          
          if (hasRightSlant && hasLeftSlant) {
            // Two crossers: DB/LB2 matches right side briefly, then picks up left side
            if (isInBreakingRoute(selectedRoute)) {
              const key = selectedRoute === 'drag' ? 'drag_wr1_handoff' : 'slant_wr1_handoff';
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, key, selectedFormation);
              dblb2RouteDuration = routeDuration;
            } else {
              const key = selectedRoute2 === 'drag' ? 'drag_wr2_handoff' : 'slant_wr2_handoff';
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, key, selectedFormation);
              dblb2RouteDuration = routeDuration2;
            }
          } else if (hasRightSlant) {
            // Only right side in-breaking - match then release, settle to zone
            if (isInBreakingRoute(selectedRoute3)) {
              const key = selectedRoute3 === 'drag' ? 'drag' : 'slant';
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, key, selectedFormation);
              dblb2RouteDuration = routeDuration3;
            } else {
              const key = selectedRoute4 === 'drag' ? 'drag_wr4' : 'slant_wr4';
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, key, selectedFormation);
              dblb2RouteDuration = routeDuration4;
            }
          } else if (hasLeftSlant) {
            // Only left side in-breaking - wait then pick up when it crosses into zone
            if (isInBreakingRoute(selectedRoute)) {
              const key = selectedRoute === 'drag' ? 'drag_wr1_handoff' : 'slant_wr1_handoff';
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, key, selectedFormation);
              dblb2RouteDuration = routeDuration;
            } else {
              const key = selectedRoute2 === 'drag' ? 'drag_wr2_handoff' : 'slant_wr2_handoff';
              dblb2Reaction = getDBLB2Reaction(selectedCoverage, key, selectedFormation);
              dblb2RouteDuration = routeDuration2;
            }
          } else {
            dblb2Reaction = getDBLB2Reaction(selectedCoverage, 'zone_drop', selectedFormation);
          }
        }
      }

      // Get SS data - uses WR3's route since SS is responsible for deep zone over WR3
      // In cover4Palms: If WR3 runs short out (CB2 takes him), SS matches WR4 on deep routes
      // In cover3Match: SS matches WR3 unless WR3 goes inside (slant), then picks up RB if RB comes to right side
      const ssStart = getSSStartPosition();
      let ssReaction = getSSReaction(selectedCoverage, selectedRoute3, selectedFormation);
      let ssRouteDuration = routeDuration3;

      const ssMovement: PositionMovementResolution | null = customDefenseAssignments
        ? resolvePositionMovement('ss', customDefenseAssignments.ss, selectedFormation, {
            keyRoute: selectedRoute3,
            keyWaypoints: mirroredWaypoints3,
            keyRouteDuration: routeDuration3,
            partnerRoute: selectedRoute4,
            partnerWaypoints: mirroredWaypoints4,
            partnerRouteDuration: routeDuration4,
            // Cover 3 apex (SS) also reads the RB (fast to the right flat); crossers ([WR1..WR4])
            // give the trips SS access to WR1 (its #1).
            rbWaypoints: rbWaypoints,
            crosserWaypoints: [waypoints, offsetWaypoints2, mirroredWaypoints3, mirroredWaypoints4],
          })
        : null;

      if (selectedCoverage === 'cover4Palms') {
        if (selectedRoute3 === 'out' && deepRoutes.includes(selectedRoute4)) {
          // WR3 runs short out (CB2 takes him), WR4 goes deep - SS matches WR4
          ssReaction = getSSReaction(selectedCoverage, selectedRoute4, selectedFormation);
          ssRouteDuration = routeDuration4;
        }
        // Otherwise SS uses default WR3 reaction
      } else if (selectedCoverage === 'man') {
        // Man coverage: SS provides deep help on right side (WR3 + WR4)
        // If multiple deep threats, stay centered — don't commit to one
        const wr3Deep = deepRoutes.includes(selectedRoute3);
        const wr4Deep = deepRoutes.includes(selectedRoute4);
        if (wr3Deep && wr4Deep) {
          ssReaction = getSSReaction(selectedCoverage, 'deep_help', selectedFormation);
          ssRouteDuration = Math.max(routeDuration3, routeDuration4);
        } else if (wr4Deep) {
          ssReaction = getSSReaction(selectedCoverage, selectedRoute4, selectedFormation);
          ssRouteDuration = routeDuration4;
        } else if (wr3Deep) {
          ssReaction = getSSReaction(selectedCoverage, selectedRoute3, selectedFormation);
          ssRouteDuration = routeDuration3;
        }
        // If neither is deep, default WR3 reaction (moderate drop)
      } else if (selectedCoverage === 'cover3Match') {
        // SS matches WR3 unless WR3 runs in-breaking route (slant/drag)
        // If WR3 runs in-breaking and RB comes to right side, SS picks up RB
        if (isInBreakingRoute(selectedRoute3)) {
          // Check if RB route goes to right side (out, swing, wheel)
          if (selectedRBRoute === 'out') {
            ssReaction = getSSReaction(selectedCoverage, 'slant_rb_out', selectedFormation);
            ssRouteDuration = loadRBRouteData(selectedRBRoute)?.duration || 1200;
          } else if (selectedRBRoute === 'swing') {
            ssReaction = getSSReaction(selectedCoverage, 'slant_rb_swing', selectedFormation);
            ssRouteDuration = loadRBRouteData(selectedRBRoute)?.duration || 1000;
          } else if (selectedRBRoute === 'wheel') {
            ssReaction = getSSReaction(selectedCoverage, 'slant_rb_wheel', selectedFormation);
            ssRouteDuration = loadRBRouteData(selectedRBRoute)?.duration || 2500;
          }
          // If RB doesn't come to right side (curl), SS uses default slant reaction (zone drop)
        }
        // Otherwise SS uses default WR3 reaction
      } else if (selectedCoverage === 'cover3') {
        // Cover 3 zone: SS drops to right flat zone — use max duration for natural pace
        ssReaction = getSSReaction(selectedCoverage, selectedRoute3, selectedFormation);
        ssRouteDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
      } else if (selectedCoverage === 'cover6') {
        if (selectedFormation === 'trips') {
          // Cover 6 trips: Single WR side Safety. Plays deep half. No match responsibilities.
          // Use a long-duration deep zone drop — read WR4 only as a vague reference.
          ssReaction = getSSReaction(selectedCoverage, 'zone_drop', selectedFormation)
            ?? getSSReaction(selectedCoverage, selectedRoute4, selectedFormation);
          ssRouteDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
        } else {
          // Cover 6 2x2 SS: deep half safety on right side
          // Default: reads WR3 and stays deep over top
          // If CB2 takes WR3 short out, SS matches WR4 on deep routes
          if (selectedRoute3 === 'out' && deepRoutes.includes(selectedRoute4)) {
            // CB2 takes WR3 short out, WR4 goes deep — SS matches WR4
            ssReaction = getSSReaction(selectedCoverage, `${selectedRoute4}_wr4`, selectedFormation);
            ssRouteDuration = routeDuration4;
          } else if (selectedRoute3 === 'out') {
            // WR3 short out but WR4 not deep — SS stays deep in zone
            ssReaction = getSSReaction(selectedCoverage, 'out', selectedFormation);
            ssRouteDuration = routeDuration3;
          }
          // Otherwise SS uses default WR3 reaction (deep help over WR3)
        }
      }

      // Get LB data
      // In man coverage: LB covers RB
      // In cover4Match: LB picks up closest WR running a slant, or drops to zone
      // In cover4Palms: LB drops in zone, matches in-breaking routes not covered by DB/LBs
      const lbStart = getLBStartPosition();
      let lbReaction = getLBReaction(selectedCoverage, selectedRBRoute, selectedFormation);

      const lbRbRouteDuration = loadRBRouteData(selectedRBRoute)?.duration ?? 0;
      // The WRs (in real field coords) are crosser candidates the LB matches when no RB to cover.
      const lbCrosserWaypoints = [waypoints, offsetWaypoints2, mirroredWaypoints3, mirroredWaypoints4];

      const lbMovement: PositionMovementResolution | null = customDefenseAssignments
        ? resolvePositionMovement('lb', customDefenseAssignments.lb, selectedFormation, {
            keyWaypoints: rbWaypoints,
            keyRouteDuration: lbRbRouteDuration,
            rbRoute: selectedRBRoute,
            rbRouteDuration: lbRbRouteDuration,
            crosserWaypoints: lbCrosserWaypoints,
          })
        : null;

      // Perception-based LB (cover4Match): "Play RB" hook read. The LB keys the RB (#3) and
      // reacts to what he sees — the RB's positions over time — manning up on a central
      // checkdown release. When there's no RB to cover (fast flat/out release or stay-in) he
      // looks for crossers and matches the first WR crossing into his hook area, else holds.
      let lbResolvedTrajectory: Position[] | null = null;
      let lbSimHorizonMs = 0;
      if (selectedCoverage === 'cover4Match') {
        lbSimHorizonMs = Math.max(lbRbRouteDuration, MIN_SIM_HORIZON_MS);
        const rbFeed = buildObservationFeed(rbWaypoints, lbSimHorizonMs);
        const lbCrosserFeeds = lbCrosserWaypoints.map((wps) => buildObservationFeed(wps, lbSimHorizonMs));
        lbResolvedTrajectory = capture('lb', resolveHookReadDetailed(lbStart, rbFeed, DEFAULT_HOOK_READ_PARAMS_LB, lbCrosserFeeds));
      } else if (isCover3MatchDoubles) {
        // LEFT inside hook: play the RB if he releases left (else let a fast flat go), otherwise
        // match the first crosser into the left hook.
        lbSimHorizonMs = cover3HorizonMs;
        const rbFeed = buildObservationFeed(rbWaypoints, lbSimHorizonMs);
        const lbCrosserFeeds = lbCrosserWaypoints.map((wps) => buildObservationFeed(wps, lbSimHorizonMs));
        lbResolvedTrajectory = capture('lb', resolveHookReadDetailed(lbStart, rbFeed, DEFAULT_HOOK_READ_PARAMS_LB_COVER3, lbCrosserFeeds));
      } else if (isCover3MatchTrips) {
        // Trips: the LB MANS the strong-side #3 (WR3). On a WR3 short out he rats to WR2 (switch
        // feed); on a WR3 in-breaker he passes off (ZONE) to DB/LB2; otherwise he mans WR3.
        lbSimHorizonMs = cover3HorizonMs;
        const wr3Feed = buildObservationFeed(mirroredWaypoints3, lbSimHorizonMs);
        const wr2Feed = buildObservationFeed(offsetWaypoints2, lbSimHorizonMs);
        lbResolvedTrajectory = capture('lb', resolveApexMatchDetailed(lbStart, wr3Feed, DEFAULT_APEX_MATCH_PARAMS_LB_TRIPS, [], [], wr2Feed));
      } else if (isCover6) {
        // Cover 6: the LB plays the hook — covers the RB (#3) or takes a crosser from the Cover 4
        // (left) side. Same "Play RB + crossers" read as the cover4Match LB.
        lbSimHorizonMs = cover3HorizonMs;
        const rbFeed = buildObservationFeed(rbWaypoints, lbSimHorizonMs);
        const crosserFeeds = [waypoints, offsetWaypoints2].map((w) => buildObservationFeed(w, lbSimHorizonMs));
        lbResolvedTrajectory = capture('lb', resolveHookReadDetailed(lbStart, rbFeed, DEFAULT_HOOK_READ_PARAMS_LB, crosserFeeds));
      } else if (isCover4Palms) {
        // Cover 4 Palms: the LB plays Hammer on #3 (RB) — man the RB on a checkdown, else look for
        // a crosser (the "Push" fallback). Same "Play RB + crossers" hook read as Cover 4 Match.
        // Doubles: all four WRs are crosser candidates; trips: only the strong (left) WR1/WR2.
        lbSimHorizonMs = cover3HorizonMs;
        const rbFeed = buildObservationFeed(rbWaypoints, lbSimHorizonMs);
        const crosserSources = isCover4PalmsTrips ? [waypoints, offsetWaypoints2] : lbCrosserWaypoints;
        const crosserFeeds = crosserSources.map((w) => buildObservationFeed(w, lbSimHorizonMs));
        lbResolvedTrajectory = capture('lb', resolveHookReadDetailed(lbStart, rbFeed, DEFAULT_HOOK_READ_PARAMS_LB, crosserFeeds));
      }

      if (selectedCoverage === 'cover4Palms') {
        // In Cover 4 Palms: LB drops in zone, but matches in-breaking routes not covered by DB/LBs
        const rbStartX = rbWaypoints[0].position.x;
        const rbOnRightSide = rbStartX > 50;
        
        // Determine which in-breaking routes are NOT covered by DB/LB1 or DB/LB2
        // DB/LB1 (left side) covers in-breaking when RB is on right side: WR1 slant, WR2 slant, RB curl
        // DB/LB2 (right side) covers in-breaking when RB is on left side: WR4 slant, WR3 slant, RB curl
        
        // Collect unmatched in-breaking routes
        const unmatchedSlants: { wr: string; startX: number; duration: number; route: string }[] = [];
        
        if (rbOnRightSide) {
          // DB/LB1 covers left side in-breaking (WR1, WR2)
          // LB needs to cover right side in-breaking (WR3, WR4)
          if (isInBreakingRoute(selectedRoute3)) {
            unmatchedSlants.push({ wr: 'wr3', startX: 70, duration: routeDuration3, route: selectedRoute3 });
          }
          if (isInBreakingRoute(selectedRoute4)) {
            unmatchedSlants.push({ wr: 'wr4', startX: 80, duration: routeDuration4, route: selectedRoute4 });
          }
        } else {
          // DB/LB2 covers right side in-breaking (WR3, WR4)
          // LB needs to cover left side in-breaking (WR1, WR2)
          if (isInBreakingRoute(selectedRoute)) {
            unmatchedSlants.push({ wr: 'wr1', startX: 20, duration: routeDuration, route: selectedRoute });
          }
          if (isInBreakingRoute(selectedRoute2)) {
            unmatchedSlants.push({ wr: 'wr2', startX: 30, duration: routeDuration2, route: selectedRoute2 });
          }
        }
        
        // Also check RB curl - it's matched by the opposite-side DB/LB, so if RB is on same side, LB may need it
        // Actually RB curl is in-breaking, so opposite-side DB/LB takes it. LB doesn't need to cover RB curl.
        
        if (unmatchedSlants.length > 0) {
          // Find closest unmatched slant to LB (LB is at x=50)
          const lbX = 50;
          let closestWR = unmatchedSlants[0];
          let closestDistance = Math.abs(unmatchedSlants[0].startX - lbX);
          
          for (const wr of unmatchedSlants) {
            const distance = Math.abs(wr.startX - lbX);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestWR = wr;
            }
          }
          
          // Get the reaction for following that specific WR's in-breaking route
          lbReaction = getLBReaction(selectedCoverage, `${closestWR.route}_${closestWR.wr}`, selectedFormation);
        } else {
          // No unmatched in-breaking routes - zone drop
          lbReaction = getLBReaction(selectedCoverage, 'zone_drop', selectedFormation);
        }
      } else if (selectedCoverage === 'cover6') {
        if (selectedFormation === 'trips') {
          // Cover 6 trips: LB plays Hammer on #3 (RB)
          // - "Push" call when RB out fast to flat → cover #2 underneath
          // - Man on RB otherwise
          const rbToFlat = selectedRBRoute === 'out' || selectedRBRoute === 'swing' || selectedRBRoute === 'wheel';
          if (rbToFlat) {
            // "Push" call — work to cover #2 (WR2) with underneath leverage
            lbReaction = getLBReaction(selectedCoverage, 'push_wr2', selectedFormation);
          } else {
            // Man on RB
            lbReaction = getLBReaction(selectedCoverage, `man_rb`, selectedFormation);
          }
        } else {
          // Cover 6 2x2: LB matches short in-breaking routes from the left (Cover 4) side only
          const leftSlants: { wr: string; route: string; startX: number }[] = [];
          if (isInBreakingRoute(selectedRoute)) {
            leftSlants.push({ wr: 'wr1', route: selectedRoute, startX: 20 });
          }
          if (isInBreakingRoute(selectedRoute2)) {
            leftSlants.push({ wr: 'wr2', route: selectedRoute2, startX: 30 });
          }

          if (leftSlants.length > 0) {
            // Find closest left-side in-breaking route to LB (LB is at x=50)
            const lbX = 50;
            let closestWR = leftSlants[0];
            let closestDistance = Math.abs(leftSlants[0].startX - lbX);
            for (const wr of leftSlants) {
              const distance = Math.abs(wr.startX - lbX);
              if (distance < closestDistance) {
                closestDistance = distance;
                closestWR = wr;
              }
            }
            const key = closestWR.route === 'drag' ? `drag_${closestWR.wr}` : `slant_${closestWR.wr}`;
            lbReaction = getLBReaction(selectedCoverage, key, selectedFormation);
          } else {
            lbReaction = getLBReaction(selectedCoverage, 'zone_drop', selectedFormation);
          }
        }
      } else if (selectedCoverage === 'cover3Match' || selectedCoverage === 'cover3') {
        // Left side slants (WR1/WR2) start in his zone - match then release when they cross out
        // Right side slants (WR3/WR4) cross into his zone - pick them up
        // RB curl goes to DB/LB2
        
        const rbCurlEndsRight = selectedRBRoute === 'curl';
        
        // Check for in-breaking routes (slant/drag)
        const hasLeftSlant = isInBreakingRoute(selectedRoute) || isInBreakingRoute(selectedRoute2);
        const hasRightSlant = isInBreakingRoute(selectedRoute3) || isInBreakingRoute(selectedRoute4);
        
        if (hasLeftSlant && hasRightSlant) {
          // Two crossers: LB matches left side briefly, then picks up right side
          if (isInBreakingRoute(selectedRoute3)) {
            const key = selectedRoute3 === 'drag' ? 'drag_wr3_handoff' : 'slant_wr3_handoff';
            lbReaction = getLBReaction(selectedCoverage, key, selectedFormation);
          } else {
            const key = selectedRoute4 === 'drag' ? 'drag_wr4_handoff' : 'slant_wr4_handoff';
            lbReaction = getLBReaction(selectedCoverage, key, selectedFormation);
          }
        } else if (hasLeftSlant) {
          // Only left side in-breaking - match then release, settle to zone
          if (isInBreakingRoute(selectedRoute)) {
            const key = selectedRoute === 'drag' ? 'drag_wr1' : 'slant_wr1';
            lbReaction = getLBReaction(selectedCoverage, key, selectedFormation);
          } else {
            const key = selectedRoute2 === 'drag' ? 'drag_wr2' : 'slant_wr2';
            lbReaction = getLBReaction(selectedCoverage, key, selectedFormation);
          }
        } else if (hasRightSlant && !rbCurlEndsRight) {
          // Only right side in-breaking - wait then pick up when it crosses into zone
          if (isInBreakingRoute(selectedRoute3)) {
            const key = selectedRoute3 === 'drag' ? 'drag_wr3_handoff' : 'slant_wr3_handoff';
            lbReaction = getLBReaction(selectedCoverage, key, selectedFormation);
          } else {
            const key = selectedRoute4 === 'drag' ? 'drag_wr4_handoff' : 'slant_wr4_handoff';
            lbReaction = getLBReaction(selectedCoverage, key, selectedFormation);
          }
        } else {
          lbReaction = getLBReaction(selectedCoverage, 'zone_drop', selectedFormation);
        }
      }

      // Set initial positions from rules systems
      const wrStart = waypoints[0].position;
      // WR2 starts next to WR1, offset by 10 horizontally and 5 back from line
      const wr2Start = { x: waypoints2[0].position.x + 10, y: waypoints2[0].position.y + 5 };

      let wr3Start: Position;
      let wr4Start: Position;

      if (selectedFormation === 'trips') {
        // Trips (3x1): WR3 on strong (left) side, WR4 alone on weak (right) side
        wr3Start = { x: waypoints3[0].position.x + 20, y: waypoints3[0].position.y + 5 };
        wr4Start = { x: 100 - waypoints4[0].position.x, y: waypoints4[0].position.y };
      } else {
        // Doubles (2x2): WR3 mirrors WR2 on right side, WR4 mirrors WR1
        wr3Start = { x: 100 - waypoints3[0].position.x - 10, y: waypoints3[0].position.y + 5 };
        wr4Start = { x: 100 - waypoints4[0].position.x, y: waypoints4[0].position.y };
      }
      // RB starts at its defined position
      const rbStart = rbWaypoints[0].position;

      // Effective start positions: when a custom-defense movement resolved to a real mode
      // (legacyReaction/perceptionTrajectory), use its own startPosition; otherwise ('hold',
      // or customDefenseAssignments absent) keep the existing legacy Start value unchanged.
      const cb1EffectiveStart = cb1Movement && cb1Movement.mode !== 'hold' ? cb1Movement.startPosition : cb1Start;
      const cb2EffectiveStart = cb2Movement && cb2Movement.mode !== 'hold' ? cb2Movement.startPosition : cb2Start;
      const dblb1EffectiveStart = dblb1Movement && dblb1Movement.mode !== 'hold' ? dblb1Movement.startPosition : dblb1Start;
      const dblb2EffectiveStart = dblb2Movement && dblb2Movement.mode !== 'hold' ? dblb2Movement.startPosition : dblb2Start;
      const fsEffectiveStart = fsMovement && fsMovement.mode !== 'hold' ? fsMovement.startPosition : fsStart;
      const ssEffectiveStart = ssMovement && ssMovement.mode !== 'hold' ? ssMovement.startPosition : ssStart;
      const lbEffectiveStart = lbMovement && lbMovement.mode !== 'hold' ? lbMovement.startPosition : lbStart;

      // Reset to starting positions immediately
      wrControls.set({
        left: `${wrStart.x}%`,
        top: `${wrStart.y}%`,
      });
      wr2Controls.set({
        left: `${wr2Start.x}%`,
        top: `${wr2Start.y}%`,
      });
      wr3Controls.set({
        left: `${wr3Start.x}%`,
        top: `${wr3Start.y}%`,
      });
      wr4Controls.set({
        left: `${wr4Start.x}%`,
        top: `${wr4Start.y}%`,
      });
      rbControls.set({
        left: `${rbStart.x}%`,
        top: `${rbStart.y}%`,
      });
      cb1Controls.set({
        left: `${cb1EffectiveStart.x}%`,
        top: `${cb1EffectiveStart.y}%`,
      });
      cb2Controls.set({
        left: `${cb2EffectiveStart.x}%`,
        top: `${cb2EffectiveStart.y}%`,
      });
      dblb1Controls.set({
        left: `${dblb1EffectiveStart.x}%`,
        top: `${dblb1EffectiveStart.y}%`,
      });
      dblb2Controls.set({
        left: `${dblb2EffectiveStart.x}%`,
        top: `${dblb2EffectiveStart.y}%`,
      });
      fsControls.set({
        left: `${fsEffectiveStart.x}%`,
        top: `${fsEffectiveStart.y}%`,
      });
      ssControls.set({
        left: `${ssEffectiveStart.x}%`,
        top: `${ssEffectiveStart.y}%`,
      });
      lbControls.set({
        left: `${lbEffectiveStart.x}%`,
        top: `${lbEffectiveStart.y}%`,
      });

      // Small delay before starting animation
      await new Promise((resolve) => setTimeout(resolve, 100));

      // (offsetWaypoints2 / mirroredWaypoints3 / mirroredWaypoints4 are computed earlier,
      // right after route data loads, so the custom-defense resolver can use them too.)

      // Perception-based CB2 (simulate-then-animate), mirror of CB1. CB2 keys WR4 and reacts
      // to what he sees — WR4's REAL (mirrored) positions over time — with no route knowledge.
      let cb2ResolvedTrajectory: Position[] | null = null;
      let cb2SimHorizonMs = 0;
      if (selectedCoverage === 'cover4Match') {
        cb2SimHorizonMs = Math.max(routeDuration4, MIN_SIM_HORIZON_MS);
        const wr4Feed = buildObservationFeed(mirroredWaypoints4, cb2SimHorizonMs);
        cb2ResolvedTrajectory = capture('cb2', resolveManMatchDetailed(cb2Start, wr4Feed, DEFAULT_MAN_MATCH_PARAMS_RIGHT));
      } else if (isCover3MatchDoubles) {
        // CB2 plays the deep right third with a MOD read on WR4 (man vertical, else drop).
        cb2SimHorizonMs = cover3HorizonMs;
        const wr4Feed = buildObservationFeed(mirroredWaypoints4, cb2SimHorizonMs);
        cb2ResolvedTrajectory = capture('cb2', resolveManMatchDetailed(cb2Start, wr4Feed, DEFAULT_MAN_MATCH_PARAMS_CB2_COVER3));
      } else if (isCover3MatchTrips) {
        // Trips: CB2 plays straight man-to-man on the single weak-side WR4 (no zone bail).
        cb2SimHorizonMs = cover3HorizonMs;
        const wr4Feed = buildObservationFeed(mirroredWaypoints4, cb2SimHorizonMs);
        cb2ResolvedTrajectory = capture('cb2', resolveManTrailDetailed(cb2Start, wr4Feed, DEFAULT_MAN_TRAIL_PARAMS));
      } else if (isCover6) {
        // Cover 6: CB2 is the Cover 2 (right) corner — matches #1 (WR4) man-to-man everywhere.
        // Same in trips (single-side man on WR4).
        cb2SimHorizonMs = cover3HorizonMs;
        const wr4Feed = buildObservationFeed(mirroredWaypoints4, cb2SimHorizonMs);
        cb2ResolvedTrajectory = capture('cb2', resolveManTrailDetailed(cb2Start, wr4Feed, DEFAULT_MAN_TRAIL_PARAMS));
      } else if (isCover4PalmsDoubles) {
        // Cover 4 Palms (2x2): CB2 plays Palms on WR4 — MOD on #1, jumps WR3's early out to the
        // right flat. Mirror of the CB1 Palms corner.
        cb2SimHorizonMs = cover3HorizonMs;
        const wr4Feed = buildObservationFeed(mirroredWaypoints4, cb2SimHorizonMs);
        const wr3Feed = buildObservationFeed(mirroredWaypoints3, cb2SimHorizonMs);
        cb2ResolvedTrajectory = capture('cb2', resolvePalmsCornerDetailed(cb2Start, wr4Feed, wr3Feed, DEFAULT_PALMS_CORNER_PARAMS_CB2));
      } else if (isCover4PalmsTrips) {
        // Cover 4 Palms (trips): CB2 is the weak corner — straight man-to-man on the single WR4.
        cb2SimHorizonMs = cover3HorizonMs;
        const wr4Feed = buildObservationFeed(mirroredWaypoints4, cb2SimHorizonMs);
        cb2ResolvedTrajectory = capture('cb2', resolveManTrailDetailed(cb2Start, wr4Feed, DEFAULT_MAN_TRAIL_PARAMS));
      }

      // Perception-based FS (keys WR2) and SS (keys WR3): same man-match read as the corners —
      // match if the #2 receiver gets vertical past ~10 yards, otherwise maintain the deep drop.
      // Feeds use each receiver's REAL field path (WR2 offset, WR3 mirrored/offset).
      let fsResolvedTrajectory: Position[] | null = null;
      let fsSimHorizonMs = 0;
      let ssResolvedTrajectory: Position[] | null = null;
      let ssSimHorizonMs = 0;
      if (selectedCoverage === 'cover4Match') {
        fsSimHorizonMs = Math.max(routeDuration2, MIN_SIM_HORIZON_MS);
        const wr2Feed = buildObservationFeed(offsetWaypoints2, fsSimHorizonMs);
        fsResolvedTrajectory = capture('fs', resolveManMatchDetailed(fsStart, wr2Feed, DEFAULT_MAN_MATCH_PARAMS_FS));

        if (selectedFormation === 'trips') {
          // Trips: WR3 is a strong-side (left) receiver, so the right SS has no #2 to key. He plays
          // the weak "poach" deep-help half instead of chasing a phantom right-side WR3 to the middle.
          ssSimHorizonMs = cover3HorizonMs;
          ssResolvedTrajectory = resolveDeepZone(ssStart, ssSimHorizonMs, DEFAULT_DEEP_ZONE_PARAMS_SS_PALMS);
        } else {
          ssSimHorizonMs = Math.max(routeDuration3, MIN_SIM_HORIZON_MS);
          const wr3Feed = buildObservationFeed(mirroredWaypoints3, ssSimHorizonMs);
          ssResolvedTrajectory = capture('ss', resolveManMatchDetailed(ssStart, wr3Feed, DEFAULT_MAN_MATCH_PARAMS_SS));
        }
      } else if (isCover3MatchDoubles) {
        // FS is the single-high deep middle third (pure zone drop, no key).
        fsSimHorizonMs = cover3HorizonMs;
        fsResolvedTrajectory = resolveDeepZone(fsStart, fsSimHorizonMs, DEFAULT_DEEP_ZONE_PARAMS_FS);

        // SS is the RIGHT apex "match #2" on WR3, reading WR4 (short out/curl) and the RB (right flat).
        ssSimHorizonMs = cover3HorizonMs;
        const wr3Feed = buildObservationFeed(mirroredWaypoints3, ssSimHorizonMs);
        const wr4Feed = buildObservationFeed(mirroredWaypoints4, ssSimHorizonMs);
        const rbFeed = buildObservationFeed(rbWaypoints, ssSimHorizonMs);
        ssResolvedTrajectory = capture('ss', resolveApexMatchDetailed(ssStart, wr3Feed, DEFAULT_APEX_MATCH_PARAMS_SS, wr4Feed, rbFeed));
      } else if (isCover3MatchTrips) {
        // FS: same single-high deep middle third.
        fsSimHorizonMs = cover3HorizonMs;
        fsResolvedTrajectory = resolveDeepZone(fsStart, fsSimHorizonMs, DEFAULT_DEEP_ZONE_PARAMS_FS);

        // Trips SS: the WEAK apex. Mans the RB on any release to the weak (right) side, otherwise
        // drops and matches crossers coming from the 3-WR (strong) side.
        ssSimHorizonMs = cover3HorizonMs;
        const rbFeed = buildObservationFeed(rbWaypoints, ssSimHorizonMs);
        const crosserFeeds = [waypoints, offsetWaypoints2, mirroredWaypoints3, mirroredWaypoints4].map((w) =>
          buildObservationFeed(w, ssSimHorizonMs)
        );
        ssResolvedTrajectory = capture('ss', resolveHookReadDetailed(ssStart, rbFeed, DEFAULT_HOOK_READ_PARAMS_SS_TRIPS, crosserFeeds));
      } else if (isCover6) {
        // Cover 6: FS is the Cover 4 (left) robber safety — reads WR2 (man vertical, else the deep
        // left quarter). SS is the Cover 2 (right) deep-HALF safety (pure deep zone).
        fsSimHorizonMs = cover3HorizonMs;
        const wr2Feed = buildObservationFeed(offsetWaypoints2, fsSimHorizonMs);
        fsResolvedTrajectory = capture('fs', resolveManMatchDetailed(fsStart, wr2Feed, DEFAULT_MAN_MATCH_PARAMS_FS));

        ssSimHorizonMs = cover3HorizonMs;
        ssResolvedTrajectory = resolveDeepZone(ssStart, ssSimHorizonMs, DEFAULT_DEEP_ZONE_PARAMS_SS_COVER6);
      } else if (isCover4Palms) {
        // Cover 4 Palms: FS is the (strong-side) robber safety — reads WR2. If WR2 runs an early
        // short out (corner's "Out" call), FS mans WR1 over the top; else robber read on WR2 (man
        // vertical, else deep quarter). Same in trips (strong side). SS is the robber safety on
        // WR3 (reading WR4) in doubles; in trips SS is the weak "poach" safety (deep-help zone).
        fsSimHorizonMs = cover3HorizonMs;
        const wr2Feed = buildObservationFeed(offsetWaypoints2, fsSimHorizonMs);
        const wr1Feed = buildObservationFeed(waypoints, fsSimHorizonMs);
        fsResolvedTrajectory = capture('fs', resolvePalmsSafetyDetailed(fsStart, wr2Feed, wr1Feed, DEFAULT_PALMS_SAFETY_PARAMS_FS));

        ssSimHorizonMs = cover3HorizonMs;
        if (isCover4PalmsTrips) {
          ssResolvedTrajectory = resolveDeepZone(ssStart, ssSimHorizonMs, DEFAULT_DEEP_ZONE_PARAMS_SS_PALMS);
        } else {
          const wr3Feed = buildObservationFeed(mirroredWaypoints3, ssSimHorizonMs);
          const wr4Feed = buildObservationFeed(mirroredWaypoints4, ssSimHorizonMs);
          ssResolvedTrajectory = capture('ss', resolvePalmsSafetyDetailed(ssStart, wr3Feed, wr4Feed, DEFAULT_PALMS_SAFETY_PARAMS_SS));
        }
      }

      // Run WR animations
      const wrAnimation = createWRAnimation(waypoints, wrControls, onWRPositionUpdate, playbackSpeed);
      const wr2Animation = createWRAnimation(offsetWaypoints2, wr2Controls, onWR2PositionUpdate, playbackSpeed);
      const wr3Animation = createWRAnimation(mirroredWaypoints3, wr3Controls, onWR3PositionUpdate, playbackSpeed);
      const wr4Animation = createWRAnimation(mirroredWaypoints4, wr4Controls, onWR4PositionUpdate, playbackSpeed);
      
      // Run RB animation
      const rbAnimation = createWRAnimation(rbWaypoints, rbControls, onRBPositionUpdate, playbackSpeed);
      
      // Run CB1 animation. When a custom-defense assignment resolved to a real mode, it takes
      // priority; otherwise fall through to the existing, unmodified legacy branching
      // (perception-based cover4Match trajectory, then route-keyed reaction, then hold).
      const cb1Animation = cb1Movement
        ? cb1Movement.mode === 'perceptionTrajectory'
          ? createCB1TrajectoryAnimation(cb1Movement.trajectory, cb1Movement.horizonMs, playbackSpeed)
          : cb1Movement.mode === 'legacyReaction'
          ? createCB1Animation(cb1Movement.reaction as CoverageReaction, cb1Movement.routeDuration, cb1EffectiveStart, playbackSpeed)
          : Promise.resolve() // 'hold'
        : cb1ResolvedTrajectory
        ? createCB1TrajectoryAnimation(cb1ResolvedTrajectory, cb1SimHorizonMs, playbackSpeed)
        : cb1Reaction
        ? createCB1Animation(cb1Reaction, cb1RouteDuration, cb1Start, playbackSpeed)
        : Promise.resolve();

      // Run DB/LB1 animation. Perception-based coverages (cover4Match) use a resolved trajectory
      // (DB/LB1 keys WR2, underneath Wall-Read); all others use the route-keyed reaction lookup.
      const dblb1Animation = dblb1Movement
        ? dblb1Movement.mode === 'perceptionTrajectory'
          ? createDBLB1TrajectoryAnimation(dblb1Movement.trajectory, dblb1Movement.horizonMs, playbackSpeed)
          : dblb1Movement.mode === 'legacyReaction'
          ? createDBLB1Animation(dblb1Movement.reaction as DBLBReaction, dblb1Movement.routeDuration, dblb1EffectiveStart, playbackSpeed)
          : Promise.resolve() // 'hold'
        : dblb1ResolvedTrajectory
        ? createDBLB1TrajectoryAnimation(dblb1ResolvedTrajectory, dblb1SimHorizonMs, playbackSpeed)
        : dblb1Reaction
        ? createDBLB1Animation(dblb1Reaction, dblb1RouteDuration, dblb1Start, playbackSpeed)
        : Promise.resolve();

      // Run FS animation. Perception-based coverages (cover4Match) use a resolved trajectory
      // (FS keys WR2); all others use the route-keyed reaction lookup.
      const fsAnimation = fsMovement
        ? fsMovement.mode === 'perceptionTrajectory'
          ? createFSTrajectoryAnimation(fsMovement.trajectory, fsMovement.horizonMs, playbackSpeed)
          : fsMovement.mode === 'legacyReaction'
          ? createFSAnimation(fsMovement.reaction as FSReaction, fsMovement.routeDuration, fsEffectiveStart, playbackSpeed)
          : Promise.resolve() // 'hold'
        : fsResolvedTrajectory
        ? createFSTrajectoryAnimation(fsResolvedTrajectory, fsSimHorizonMs, playbackSpeed)
        : fsReaction
        ? createFSAnimation(fsReaction, fsRouteDuration, fsStart, playbackSpeed)
        : Promise.resolve();

      // Run CB2 animation. Perception-based coverages (cover4Match) use a resolved trajectory;
      // all others use the route-keyed reaction lookup.
      const cb2Animation = cb2Movement
        ? cb2Movement.mode === 'perceptionTrajectory'
          ? createCB2TrajectoryAnimation(cb2Movement.trajectory, cb2Movement.horizonMs, playbackSpeed)
          : cb2Movement.mode === 'legacyReaction'
          ? createCB2Animation(cb2Movement.reaction as CB2Reaction, cb2Movement.routeDuration, cb2EffectiveStart, playbackSpeed)
          : Promise.resolve() // 'hold'
        : cb2ResolvedTrajectory
        ? createCB2TrajectoryAnimation(cb2ResolvedTrajectory, cb2SimHorizonMs, playbackSpeed)
        : cb2Reaction
        ? createCB2Animation(cb2Reaction, cb2RouteDuration, cb2Start, playbackSpeed)
        : Promise.resolve();

      // Run DB/LB2 animation. Perception-based coverages (cover4Match) use a resolved trajectory
      // (DB/LB2 keys WR3, underneath Wall-Read); all others use the route-keyed reaction lookup.
      const dblb2Animation = dblb2Movement
        ? dblb2Movement.mode === 'perceptionTrajectory'
          ? createDBLB2TrajectoryAnimation(dblb2Movement.trajectory, dblb2Movement.horizonMs, playbackSpeed)
          : dblb2Movement.mode === 'legacyReaction'
          ? createDBLB2Animation(dblb2Movement.reaction as DBLB2Reaction, dblb2Movement.routeDuration, dblb2EffectiveStart, playbackSpeed)
          : Promise.resolve() // 'hold'
        : dblb2ResolvedTrajectory
        ? createDBLB2TrajectoryAnimation(dblb2ResolvedTrajectory, dblb2SimHorizonMs, playbackSpeed)
        : dblb2Reaction
        ? createDBLB2Animation(dblb2Reaction, dblb2RouteDuration, dblb2Start, playbackSpeed)
        : Promise.resolve();

      // Run SS animation. Perception-based coverages (cover4Match) use a resolved trajectory
      // (SS keys WR3); all others use the route-keyed reaction lookup.
      const ssAnimation = ssMovement
        ? ssMovement.mode === 'perceptionTrajectory'
          ? createSSTrajectoryAnimation(ssMovement.trajectory, ssMovement.horizonMs, playbackSpeed)
          : ssMovement.mode === 'legacyReaction'
          ? createSSAnimation(ssMovement.reaction as SSReaction, ssMovement.routeDuration, ssEffectiveStart, playbackSpeed)
          : Promise.resolve() // 'hold'
        : ssResolvedTrajectory
        ? createSSTrajectoryAnimation(ssResolvedTrajectory, ssSimHorizonMs, playbackSpeed)
        : ssReaction
        ? createSSAnimation(ssReaction, ssRouteDuration, ssStart, playbackSpeed)
        : Promise.resolve();

      // Run LB animation
      // In man coverage: uses RB's route duration
      // In cover4Match/cover4Palms: uses the duration of the WR running the slant that LB is covering
      let lbAnimationDuration: number;
      if (selectedCoverage === 'cover4Palms') {
        // Determine which WR's slant the LB is covering (unmatched by DB/LBs)
        const rbStartX = rbWaypoints[0].position.x;
        const rbOnRightSide = rbStartX > 50;
        
        // Find the unmatched slant that LB is covering
        const unmatchedSlants: { wr: string; startX: number; duration: number }[] = [];
        if (rbOnRightSide) {
          // LB covers right side in-breaking (WR3, WR4)
          if (isInBreakingRoute(selectedRoute3)) {
            unmatchedSlants.push({ wr: 'wr3', startX: 70, duration: routeDuration3 });
          }
          if (isInBreakingRoute(selectedRoute4)) {
            unmatchedSlants.push({ wr: 'wr4', startX: 80, duration: routeDuration4 });
          }
        } else {
          // LB covers left side in-breaking (WR1, WR2)
          if (isInBreakingRoute(selectedRoute)) {
            unmatchedSlants.push({ wr: 'wr1', startX: 20, duration: routeDuration });
          }
          if (isInBreakingRoute(selectedRoute2)) {
            unmatchedSlants.push({ wr: 'wr2', startX: 30, duration: routeDuration2 });
          }
        }
        
        if (unmatchedSlants.length > 0) {
          // Find closest slant to LB (LB is at x=50)
          const lbX = 50;
          let closestWR = unmatchedSlants[0];
          let closestDistance = Math.abs(unmatchedSlants[0].startX - lbX);
          for (const wr of unmatchedSlants) {
            const distance = Math.abs(wr.startX - lbX);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestWR = wr;
            }
          }
          lbAnimationDuration = closestWR.duration;
        } else {
          // Zone drop - use WR1's duration as default
          lbAnimationDuration = routeDuration;
        }
      } else if (selectedCoverage === 'cover3Match' || selectedCoverage === 'cover3') {
        // LB uses handoff logic - matches left side slants then hands off, or picks up right side handoffs
        const leftSlants: { wr: string; startX: number; duration: number }[] = [];
        if (isInBreakingRoute(selectedRoute)) {
          leftSlants.push({ wr: 'wr1', startX: 20, duration: routeDuration });
        }
        if (isInBreakingRoute(selectedRoute2)) {
          leftSlants.push({ wr: 'wr2', startX: 30, duration: routeDuration2 });
        }
        
        const rightSlants: { wr: string; startX: number; duration: number }[] = [];
        if (isInBreakingRoute(selectedRoute3)) {
          rightSlants.push({ wr: 'wr3', startX: 70, duration: routeDuration3 });
        }
        if (isInBreakingRoute(selectedRoute4)) {
          rightSlants.push({ wr: 'wr4', startX: 80, duration: routeDuration4 });
        }
        
        const rbCurlEndsRight = selectedRBRoute === 'curl';
        
        if (leftSlants.length > 0 && rightSlants.length > 0) {
          // Two crossers - use the longer duration
          lbAnimationDuration = Math.max(leftSlants[0].duration, rightSlants[0].duration);
        } else if (leftSlants.length > 0) {
          lbAnimationDuration = leftSlants[0].duration;
        } else if (rightSlants.length > 0 && !rbCurlEndsRight) {
          lbAnimationDuration = rightSlants[0].duration;
        } else {
          // Zone drop - use longest route duration so LB drops at same pace as FS/CBs
          lbAnimationDuration = Math.max(routeDuration, routeDuration2, routeDuration3, routeDuration4);
        }
      } else if (selectedCoverage === 'cover6') {
        if (selectedFormation === 'trips') {
          // Cover 6 trips: LB plays Hammer on RB
          // Use RB duration when running with RB, or WR2 duration when "Push" call (covering #2)
          const rbToFlat = selectedRBRoute === 'out' || selectedRBRoute === 'swing' || selectedRBRoute === 'wheel';
          if (rbToFlat) {
            lbAnimationDuration = routeDuration2;
          } else {
            lbAnimationDuration = loadRBRouteData(selectedRBRoute)?.duration || 1000;
          }
        } else {
          // Cover 6 2x2: LB matches left side in-breaking routes — use the matched WR's duration
          if (isInBreakingRoute(selectedRoute)) {
            lbAnimationDuration = routeDuration;
          } else if (isInBreakingRoute(selectedRoute2)) {
            lbAnimationDuration = routeDuration2;
          } else {
            // Zone drop — use WR1's duration as default
            lbAnimationDuration = routeDuration;
          }
        }
      } else {
        // Man coverage - use RB's route duration
        lbAnimationDuration = loadRBRouteData(selectedRBRoute)?.duration || 1000;
      }
      const lbAnimation = lbMovement
        ? lbMovement.mode === 'perceptionTrajectory'
          ? createLBTrajectoryAnimation(lbMovement.trajectory, lbMovement.horizonMs, playbackSpeed)
          : lbMovement.mode === 'legacyReaction'
          ? createLBAnimation(lbMovement.reaction as LBReaction, lbMovement.routeDuration, lbEffectiveStart, playbackSpeed)
          : Promise.resolve() // 'hold'
        : lbResolvedTrajectory
        ? createLBTrajectoryAnimation(lbResolvedTrajectory, lbSimHorizonMs, playbackSpeed)
        : lbReaction
        ? createLBAnimation(lbReaction, lbAnimationDuration, lbStart, playbackSpeed)
        : Promise.resolve();

      // Dev-only: hand the resolved reads to the debug overlay.
      //
      // A position can be traced by either path, so pick the one that actually drove the
      // animation: the custom-defense resolution wins when present, exactly as the animation
      // selectors above prefer `xMovement` over `xResolvedTrajectory`. Otherwise fall back to the
      // built-in coverage's trace. Positions with neither (a spot-drop deep zone, a legacy
      // keyframe reaction) are omitted rather than shown as having made no decisions.
      if (onReadTrace) {
        const movements: Array<[DefensePositionId, PositionMovementResolution | null]> = [
          ['cb1', cb1Movement], ['dblb1', dblb1Movement], ['fs', fsMovement],
          ['ss', ssMovement], ['dblb2', dblb2Movement], ['cb2', cb2Movement], ['lb', lbMovement],
        ];
        const reads: Partial<Record<DefensePositionId, { finalState: ReadState; trace: ReadTraceEntry[] }>> = {};
        for (const [position, movement] of movements) {
          const trace =
            movement && movement.mode === 'perceptionTrajectory' && movement.trace
              ? movement.trace
              : movement
              ? null // a custom assignment drove this position but carries no trace
              : builtInTraces[position] ?? null;
          if (!trace) continue;
          const decisions = trace.filter((e) => e.transition);
          reads[position] = {
            finalState: decisions.length ? decisions[decisions.length - 1].state : 'READ',
            trace,
          };
        }
        onReadTrace(reads);
      }

      await Promise.all([wrAnimation, wr2Animation, wr3Animation, wr4Animation, rbAnimation, cb1Animation, cb2Animation, dblb1Animation, dblb2Animation, fsAnimation, ssAnimation, lbAnimation]);

      // Animation complete
      onAnimationComplete();
    } finally {
      animationRef.current = false;
    }
  }, [
    selectedRoute,
    selectedRoute2,
    selectedRoute3,
    selectedRoute4,
    selectedRBRoute,
    selectedCoverage,
    selectedFormation,
    customDefenseAssignments,
    playbackSpeed,
    loadRouteData,
    loadRBRouteData,
    loadCoverageData,
    wrControls,
    wr2Controls,
    wr3Controls,
    wr4Controls,
    rbControls,
    cb1Controls,
    cb2Controls,
    dblb1Controls,
    dblb2Controls,
    fsControls,
    ssControls,
    lbControls,
    createWRAnimation,
    createCB1Animation,
    createCB1TrajectoryAnimation,
    createCB2Animation,
    createCB2TrajectoryAnimation,
    createDBLB1Animation,
    createDBLB1TrajectoryAnimation,
    createDBLB2Animation,
    createDBLB2TrajectoryAnimation,
    createFSAnimation,
    createFSTrajectoryAnimation,
    createSSAnimation,
    createSSTrajectoryAnimation,
    createLBAnimation,
    createLBTrajectoryAnimation,
    getDBLB1StartPosition,
    getDBLB2StartPosition,
    getFSStartPosition,
    getSSStartPosition,
    getLBStartPosition,
    getCB2StartPosition,
    onAnimationComplete,
    onWRPositionUpdate,
    onWR2PositionUpdate,
    onWR3PositionUpdate,
    onWR4PositionUpdate,
    onRBPositionUpdate,
    onReadTrace,
  ]);

  /**
   * Start animation when isPlaying becomes true
   */
  useEffect(() => {
    if (isPlaying && !animationRef.current) {
      executeSimulation();
    }
  }, [isPlaying, executeSimulation]);

  /**
   * Reset positions when not playing
   */
  useEffect(() => {
    if (!isPlaying) {
      const waypoints = getRouteWaypoints(selectedRoute);
      const waypoints2 = getRouteWaypoints(selectedRoute2);
      const wrStart = waypoints[0].position;
      const wr2Start = { x: waypoints2[0].position.x + 10, y: waypoints2[0].position.y + 5 };
      const cb1Start = getCB1StartPosition();
      const cb2Start = getCB2StartPosition();
      const dblb1Start = getDBLB1StartPosition();
      const dblb2Start = getDBLB2StartPosition();
      const fsStart = getFSStartPosition();
      const ssStart = getSSStartPosition();
      const rbWaypoints = getRBRouteWaypoints(selectedRBRoute);
      const rbStart = rbWaypoints[0].position;

      wrControls.set({
        left: `${wrStart.x}%`,
        top: `${wrStart.y}%`,
      });
      wr2Controls.set({
        left: `${wr2Start.x}%`,
        top: `${wr2Start.y}%`,
      });
      
      // WR3 and WR4 positions depend on formation
      const waypoints3 = getRouteWaypoints(selectedRoute3);
      const waypoints4 = getRouteWaypoints(selectedRoute4);

      let wr3Start: Position;
      let wr4Start: Position;

      if (selectedFormation === 'trips') {
        wr3Start = { x: waypoints3[0].position.x + 20, y: waypoints3[0].position.y + 5 };
        wr4Start = { x: 100 - waypoints4[0].position.x, y: waypoints4[0].position.y };
      } else {
        wr3Start = { x: 100 - waypoints3[0].position.x - 10, y: waypoints3[0].position.y + 5 };
        wr4Start = { x: 100 - waypoints4[0].position.x, y: waypoints4[0].position.y };
      }
      
      wr3Controls.set({
        left: `${wr3Start.x}%`,
        top: `${wr3Start.y}%`,
      });
      wr4Controls.set({
        left: `${wr4Start.x}%`,
        top: `${wr4Start.y}%`,
      });
      rbControls.set({
        left: `${rbStart.x}%`,
        top: `${rbStart.y}%`,
      });
      cb1Controls.set({
        left: `${cb1Start.x}%`,
        top: `${cb1Start.y}%`,
      });
      cb2Controls.set({
        left: `${cb2Start.x}%`,
        top: `${cb2Start.y}%`,
      });
      dblb1Controls.set({
        left: `${dblb1Start.x}%`,
        top: `${dblb1Start.y}%`,
      });
      dblb2Controls.set({
        left: `${dblb2Start.x}%`,
        top: `${dblb2Start.y}%`,
      });
      fsControls.set({
        left: `${fsStart.x}%`,
        top: `${fsStart.y}%`,
      });
      ssControls.set({
        left: `${ssStart.x}%`,
        top: `${ssStart.y}%`,
      });
      const lbStart = getLBStartPosition();
      lbControls.set({
        left: `${lbStart.x}%`,
        top: `${lbStart.y}%`,
      });
    }
  }, [isPlaying, selectedFormation, selectedRoute, selectedRoute2, selectedRoute3, selectedRoute4, selectedRBRoute, getCB1StartPosition, getCB2StartPosition, getDBLB1StartPosition, getDBLB2StartPosition, getFSStartPosition, getSSStartPosition, getLBStartPosition, wrControls, wr2Controls, wr3Controls, wr4Controls, rbControls, cb1Controls, cb2Controls, dblb1Controls, dblb2Controls, fsControls, ssControls, lbControls]);

  // Get initial positions for rendering
  const waypoints = getRouteWaypoints(selectedRoute);
  const waypoints2 = getRouteWaypoints(selectedRoute2);
  const waypoints3 = getRouteWaypoints(selectedRoute3);
  const waypoints4 = getRouteWaypoints(selectedRoute4);
  const rbWaypoints = getRBRouteWaypoints(selectedRBRoute);
  const wrStartPosition = waypoints[0].position;
  const wr2StartPosition = { x: waypoints2[0].position.x + 10, y: waypoints2[0].position.y + 5 };
  const wr3StartPosition = selectedFormation === 'trips'
    ? { x: waypoints3[0].position.x + 20, y: waypoints3[0].position.y + 5 }
    : { x: 100 - waypoints3[0].position.x - 10, y: waypoints3[0].position.y + 5 };
  const wr4StartPosition = { x: 100 - waypoints4[0].position.x, y: waypoints4[0].position.y };
  const rbStartPosition = rbWaypoints[0].position;
  const cb1StartPosition = getCB1StartPosition();
  const cb2StartPosition = getCB2StartPosition();
  const dblb1StartPosition = getDBLB1StartPosition();
  const dblb2StartPosition = getDBLB2StartPosition();
  const fsStartPosition = getFSStartPosition();
  const ssStartPosition = getSSStartPosition();
  const lbStartPosition = getLBStartPosition();

  /**
   * Which player's on-field route picker is open, if any.
   *
   * One shared value rather than the five independent booleans this used to hold. The pickers open
   * on CLICK now instead of hover, and with five booleans a click-to-open control leaves every
   * previously opened picker on screen — hover was self-limiting only because leaving the glyph
   * closed it. A single value makes "only one open at a time" structural.
   *
   * On why it is click at all: hover does not exist on a tablet, which is the device a coach
   * actually holds, and a hover-only control cannot be reached from the keyboard. The labelled
   * dropdowns in RouteControls are now the primary path; this stays as an on-field shortcut.
   */
  const [openRoutePicker, setOpenRoutePicker] = useState<RoutePickerId | null>(null);

  // Tooltip state for each player position
  const [hoveredPlayer, setHoveredPlayer] = useState<PlayerPositionId | null>(null);

  // A picker must not stay open across a snap, and nothing on the field is editable mid-play.
  useEffect(() => {
    if (isPlaying) setOpenRoutePicker(null);
  }, [isPlaying]);

  /**
   * Close on any click that is not inside a picker, and on Escape.
   *
   * Hover needed no dismissal; click does. Listening on the container rather than the document
   * would miss clicks on the surrounding page, which is exactly where someone clicks to mean
   * "never mind".
   */
  useEffect(() => {
    if (!openRoutePicker) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest('.route-picker') && !target?.closest('[data-route-picker-trigger]')) {
        setOpenRoutePicker(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenRoutePicker(null);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openRoutePicker]);

  /**
   * The props that turn a player glyph into a real, keyboard-operable disclosure button.
   *
   * Shared by all five skill players so the behaviour cannot drift between them — the previous
   * hover handlers were copy-pasted five times and had already diverged slightly.
   */
  const routePickerTriggerProps = (id: RoutePickerId) => ({
    'data-route-picker-trigger': id,
    role: 'button' as const,
    // Focusable only when it does something. Mid-play the glyph is not interactive.
    tabIndex: isPlaying ? -1 : 0,
    'aria-haspopup': 'menu' as const,
    'aria-expanded': openRoutePicker === id,
    onClick: (event: React.MouseEvent) => {
      if (isPlaying) return;
      // Without this, a click that closes the picker via the option button below immediately
      // bubbles up and re-opens it.
      event.stopPropagation();
      setOpenRoutePicker((current) => (current === id ? null : id));
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      if (isPlaying) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setOpenRoutePicker((current) => (current === id ? null : id));
      } else if (event.key === 'Escape') {
        setOpenRoutePicker(null);
      }
    },
    style: { cursor: isPlaying ? 'default' : 'pointer' },
  });

  /**
   * The on-field route menu for one player.
   *
   * Previously written out five times with small differences between the copies. One function means
   * the option markup, the close-on-select behaviour and the event handling are identical for every
   * player.
   */
  const renderRoutePicker = (
    id: RoutePickerId,
    routeIds: string[],
    currentRoute: string,
    routeName: (routeId: string) => string | undefined,
    onSelect: ((routeId: string) => void) | undefined
  ) => {
    if (openRoutePicker !== id || isPlaying) return null;

    return (
      <div className="route-picker" role="menu">
        {routeIds.map((routeId) => (
          <button
            key={routeId}
            type="button"
            role="menuitem"
            className={`route-picker-option ${routeId === currentRoute ? 'active' : ''}`}
            aria-current={routeId === currentRoute}
            onClick={(event) => {
              // Required, not defensive. This button is a CHILD of the trigger, so without it the
              // click bubbles to the trigger's toggle, which sees the picker already closed by the
              // line below and re-opens it.
              event.stopPropagation();
              onSelect?.(routeId);
              setOpenRoutePicker(null);
            }}
          >
            {routeName(routeId) ?? routeId}
          </button>
        ))}
      </div>
    );
  };

  /** Accessible name for a skill player: who he is, what he is running, and that it can change. */
  const skillPlayerLabel = (who: string, route: string) =>
    `${who}, ${getRouteDefinition(route)?.name ?? route}. Change route.`;

  // Helper to get description for a position
  const getDescription = useCallback((positionId: PlayerPositionId): PositionDescription => {
    return getPositionDescription(positionId, selectedCoverage, selectedFormation);
  }, [selectedCoverage, selectedFormation]);

  // Tooltip component for player hover
  const renderTooltip = (positionId: PlayerPositionId) => {
    if (hoveredPlayer !== positionId) return null;
    const description = getDescription(positionId);
    return (
      <div 
        className="player-tooltip"
        role="tooltip"
      >
        <div className="player-tooltip-title">{description.title}</div>
        <div className="player-tooltip-description">{description.description}</div>
      </div>
    );
  };

  return (
    <div 
      className="animation-engine" 
      data-testid="animation-engine"
      data-is-playing={isPlaying}
    >
      {/* WR1 Player */}
      <motion.div
        className={`player player-wr ${isPlaying ? 'playing' : ''} ${openRoutePicker === 'wr1' ? 'selected' : ''}`}
        animate={wrControls}
        initial={{
          left: `${wrStartPosition.x}%`,
          top: `${wrStartPosition.y}%`,
        }}
        aria-label={skillPlayerLabel('Wide Receiver 1', selectedRoute)}
        data-testid="player-wr"
        data-visible="true"
        {...routePickerTriggerProps('wr1')}
      >
        <span className="player-icon">×</span>
        <span className="player-label">WR1</span>
        {renderRoutePicker(
          'wr1',
          getAvailableRoutes(),
          selectedRoute,
          (routeId) => getRouteDefinition(routeId)?.name,
          onWR1RouteChange as ((routeId: string) => void) | undefined
        )}
      </motion.div>

      {/* WR2 Player */}
      <motion.div
        className={`player player-wr2 ${isPlaying ? 'playing' : ''} ${openRoutePicker === 'wr2' ? 'selected' : ''}`}
        animate={wr2Controls}
        initial={{
          left: `${wr2StartPosition.x}%`,
          top: `${wr2StartPosition.y}%`,
        }}
        aria-label={skillPlayerLabel('Wide Receiver 2', selectedRoute2)}
        data-testid="player-wr2"
        data-visible="true"
        {...routePickerTriggerProps('wr2')}
      >
        <span className="player-icon">×</span>
        <span className="player-label">WR2</span>
        {renderRoutePicker(
          'wr2',
          getAvailableRoutes(),
          selectedRoute2,
          (routeId) => getRouteDefinition(routeId)?.name,
          onWR2RouteChange as ((routeId: string) => void) | undefined
        )}
      </motion.div>

      {/* WR3 Player - right side */}
      <motion.div
        className={`player player-wr3 ${isPlaying ? 'playing' : ''} ${openRoutePicker === 'wr3' ? 'selected' : ''}`}
        animate={wr3Controls}
        initial={{
          left: `${wr3StartPosition.x}%`,
          top: `${wr3StartPosition.y}%`,
        }}
        aria-label={skillPlayerLabel('Wide Receiver 3', selectedRoute3)}
        data-testid="player-wr3"
        data-visible="true"
        {...routePickerTriggerProps('wr3')}
      >
        <span className="player-icon">×</span>
        <span className="player-label">WR3</span>
        {renderRoutePicker(
          'wr3',
          getAvailableRoutes(),
          selectedRoute3,
          (routeId) => getRouteDefinition(routeId)?.name,
          onWR3RouteChange as ((routeId: string) => void) | undefined
        )}
      </motion.div>

      {/* WR4 Player - right side */}
      <motion.div
        className={`player player-wr4 ${isPlaying ? 'playing' : ''} ${openRoutePicker === 'wr4' ? 'selected' : ''}`}
        animate={wr4Controls}
        initial={{
          left: `${wr4StartPosition.x}%`,
          top: `${wr4StartPosition.y}%`,
        }}
        aria-label={skillPlayerLabel('Wide Receiver 4', selectedRoute4)}
        data-testid="player-wr4"
        data-visible="true"
        {...routePickerTriggerProps('wr4')}
      >
        <span className="player-icon">×</span>
        <span className="player-label">WR4</span>
        {renderRoutePicker(
          'wr4',
          getAvailableRoutes(),
          selectedRoute4,
          (routeId) => getRouteDefinition(routeId)?.name,
          onWR4RouteChange as ((routeId: string) => void) | undefined
        )}
      </motion.div>

      {/* CB1 Player */}
      <motion.div
        className={`player player-cb1 ${isPlaying ? 'playing' : ''}`}
        animate={cb1Controls}
        initial={{
          left: `${cb1StartPosition.x}%`,
          top: `${cb1StartPosition.y}%`,
        }}
        role="img"
        aria-label="Cornerback 1"
        data-testid="player-cb1"
        data-visible="true"
        onMouseEnter={() => setHoveredPlayer('cb1')}
        onMouseLeave={() => setHoveredPlayer(null)}
      >
        <span className="player-icon">○</span>
        <span className="player-label">CB1</span>
        {renderTooltip('cb1')}
      </motion.div>

      {/* DB/LB1 Player */}
      <motion.div
        className={`player player-dblb1 ${isPlaying ? 'playing' : ''}`}
        animate={dblb1Controls}
        initial={{
          left: `${dblb1StartPosition.x}%`,
          top: `${dblb1StartPosition.y}%`,
        }}
        role="img"
        aria-label="Apex 1, the nickel or linebacker over the left slot"
        data-testid="player-dblb1"
        data-visible="true"
        onMouseEnter={() => setHoveredPlayer('dblb1')}
        onMouseLeave={() => setHoveredPlayer(null)}
      >
        <span className="player-icon">○</span>
        <span className="player-label">APEX1</span>
        {renderTooltip('dblb1')}
      </motion.div>

      {/* FS Player */}
      <motion.div
        className={`player player-fs ${isPlaying ? 'playing' : ''}`}
        animate={fsControls}
        initial={{
          left: `${fsStartPosition.x}%`,
          top: `${fsStartPosition.y}%`,
        }}
        role="img"
        aria-label="Free Safety"
        data-testid="player-fs"
        data-visible="true"
        onMouseEnter={() => setHoveredPlayer('fs')}
        onMouseLeave={() => setHoveredPlayer(null)}
      >
        <span className="player-icon">○</span>
        <span className="player-label">FS</span>
        {renderTooltip('fs')}
      </motion.div>

      {/* CB2 Player */}
      <motion.div
        className={`player player-cb2 ${isPlaying ? 'playing' : ''}`}
        animate={cb2Controls}
        initial={{
          left: `${cb2StartPosition.x}%`,
          top: `${cb2StartPosition.y}%`,
        }}
        role="img"
        aria-label="Cornerback 2"
        data-testid="player-cb2"
        data-visible="true"
        onMouseEnter={() => setHoveredPlayer('cb2')}
        onMouseLeave={() => setHoveredPlayer(null)}
      >
        <span className="player-icon">○</span>
        <span className="player-label">CB2</span>
        {renderTooltip('cb2')}
      </motion.div>

      {/* DB/LB2 Player */}
      <motion.div
        className={`player player-dblb2 ${isPlaying ? 'playing' : ''}`}
        animate={dblb2Controls}
        initial={{
          left: `${dblb2StartPosition.x}%`,
          top: `${dblb2StartPosition.y}%`,
        }}
        role="img"
        aria-label="Apex 2, the nickel or linebacker over the right slot"
        data-testid="player-dblb2"
        data-visible="true"
        onMouseEnter={() => setHoveredPlayer('dblb2')}
        onMouseLeave={() => setHoveredPlayer(null)}
      >
        <span className="player-icon">○</span>
        <span className="player-label">APEX2</span>
        {renderTooltip('dblb2')}
      </motion.div>

      {/* SS Player */}
      <motion.div
        className={`player player-ss ${isPlaying ? 'playing' : ''}`}
        animate={ssControls}
        initial={{
          left: `${ssStartPosition.x}%`,
          top: `${ssStartPosition.y}%`,
        }}
        role="img"
        aria-label="Strong Safety"
        data-testid="player-ss"
        data-visible="true"
        onMouseEnter={() => setHoveredPlayer('ss')}
        onMouseLeave={() => setHoveredPlayer(null)}
      >
        <span className="player-icon">○</span>
        <span className="player-label">SS</span>
        {renderTooltip('ss')}
      </motion.div>

      {/* LB - animated linebacker covering RB */}
      <motion.div
        className={`player player-lb ${isPlaying ? 'playing' : ''}`}
        animate={lbControls}
        initial={{
          left: `${lbStartPosition.x}%`,
          top: `${lbStartPosition.y}%`,
        }}
        role="img"
        aria-label="Linebacker"
        data-testid="player-lb"
        data-visible="true"
        onMouseEnter={() => setHoveredPlayer('lb')}
        onMouseLeave={() => setHoveredPlayer(null)}
      >
        <span className="player-icon">○</span>
        <span className="player-label">LB</span>
        {renderTooltip('lb')}
      </motion.div>

      {/* Center - static player on line of scrimmage */}
      <div
        className="player player-center"
        style={{
          left: '50%',
          top: '85%',
        }}
        role="img"
        aria-label="Center"
        data-testid="player-center"
      >
        <span className="player-icon">□</span>
        <span className="player-label">C</span>
      </div>

      {/* QB - static player behind center */}
      <div
        className="player player-qb"
        style={{
          left: '50%',
          top: '92%',
        }}
        role="img"
        aria-label="Quarterback"
        data-testid="player-qb"
      >
        <span className="player-icon">×</span>
        <span className="player-label">QB</span>
      </div>

      {/* RB - animated player to the right of QB */}
      <motion.div
        className={`player player-rb ${isPlaying ? 'playing' : ''} ${openRoutePicker === 'rb' ? 'selected' : ''}`}
        animate={rbControls}
        initial={{
          left: `${rbStartPosition.x}%`,
          top: `${rbStartPosition.y}%`,
        }}
        aria-label={`Running Back, ${getRBRouteDefinition(selectedRBRoute)?.name ?? selectedRBRoute}. Change route.`}
        data-testid="player-rb"
        data-visible="true"
        {...routePickerTriggerProps('rb')}
      >
        <span className="player-icon">×</span>
        <span className="player-label">RB</span>
        {renderRoutePicker(
          'rb',
          getAvailableRBRoutes(),
          selectedRBRoute,
          (routeId) => getRBRouteDefinition(routeId)?.name,
          onRBRouteChange as ((routeId: string) => void) | undefined
        )}
      </motion.div>
    </div>
  );
};

export default AnimationEngine;
